/**
 * Лента сообщений (STEP8 эт.2 — Telegram-стиль).
 *
 * Пузыри свои/чужие, группировка подряд идущих, время внутри пузыря,
 * реплаи с полоской акцентом, разделители дат. Техническая информация
 * (id событий, коды) убрана из ленты по умолчанию — включается флагом
 * отладки (src/debug.ts).
 *
 * Два неочевидных места, поэтому с комментариями:
 *  1) автоскролл — прилипаем к низу, только если пользователь и так был внизу,
 *     иначе чтение истории будет дёргаться на каждом новом сообщении;
 *  2) подгрузка вверх — после вставки старых событий надо вернуть скролл на то
 *     же сообщение, иначе список «прыгает» и подгрузка срабатывает лавиной.
 */
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { Messages } from "../i18n/messages";
type TKey = keyof Messages;
import { currentLocale, tf, useT, useTF } from "../i18n";
import { NotificationCountType, type MatrixClient, type Room } from "matrix-js-sdk";

import { DEBUG, debugLog } from "../debug";
import { canRedactEvent } from "../matrix/permissions";
import { useMatrixClient } from "../matrix/clientContext";
import { canForwardMessage } from "../matrix/forward";
import { ForwardDialog } from "./ForwardDialog";
import { useAuthedAvatar, useAuthedMedia } from "../matrix/media";
import type { ReactionChip, TimelineItem } from "../matrix/timeline";
import { MediaViewer, fileIconOf, humanSize, type ViewerMedia } from "./MediaViewer";
import { appPermalink } from "./router";
import { EmojiPicker } from "./EmojiPicker";
import { IconPhone, IconPhoneOff } from "./icons";
import { canShareText, shareText } from "./share";
import { palette, s } from "./styles";
import { color, colorOf, font, radius, space } from "./theme";
import { bubbleCss, readAvatarsCss } from "./bubbleCss";
import { assignReaders, useReadReceipts } from "./readAvatars";
import { timelineCss } from "./timelineCss";
import { blobFromObjectUrl, downloadBlobViaServiceWorker } from "../downloads/serviceWorkerDownload";
import { describeDownloadError, downloadInBrowser, isPlainBrowser } from "../downloads/browserDownload";

/**
 * Аватар читателя в отметке прочтения (Антон, 2026-09-14).
 *
 * Раньше здесь были ТОЛЬКО инициалы — картинка не рисовалась вообще,
 * ни при каких условиях. Теперь: есть mxc — грузим через useAuthedAvatar,
 * нет mxc или не загрузился — кружок с буквами.
 */
function ReaderAvatar({
  client,
  userId,
  name,
  initials,
  avatarMxc,
}: {
  client: MatrixClient;
  userId: string;
  name: string;
  initials: string;
  avatarMxc: string | null;
}) {
  const url = useAuthedAvatar(client, avatarMxc);
  const tfmt = useTF();

  return (
    <span
      className="alg-reader"
      style={{
        background: url ? "transparent" : colorOf(userId),
        overflow: "hidden",
      }}
      title={tfmt("timeline.readUpToHere", { name })}
      data-user-id={userId}
    >
      {url ? (
        <img
          src={url}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        initials
      )}
    </span>
  );
}

// ДАТЫ ПО ЯЗЫКУ ИНТЕРФЕЙСА (решение Антона, фаза 2б): было жёсткое "ru-RU",
// из-за чего время и даты оставались русскими при любом языке. Это чистые
// функции вне компонента, поэтому currentLocale(), а не хук useLocale().
// hourCycle h23: всегда «21:37», а не «09:37 PM». На английской системе
// Антона 12-часовой формат с AM/PM был шире резерва под время в пузыре
// (padding-right: 60px) и налезал на текст коротких сообщений
// (скриншот Антона 2026-09-17 21:38). 24-часовой формат — 5 знаков в любой
// локали, резерва хватает всегда.
function timeOf(ts: number): string {
  return new Date(ts).toLocaleTimeString(currentLocale(), {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
}



/**
 * Миниатюра в цитате: 32px, object-fit: cover, скругление.
 * Грузим через useAuthedMedia — Bearer-заголовок обязателен.
 */
function ReplyThumb({ client, mxc }: { client: MatrixClient; mxc: string }) {
  const { url } = useAuthedMedia(client, mxc);
  if (!url) return null;
  return (
    <img
      src={url}
      alt=""
      style={{
        width: 32,
        height: 32,
        borderRadius: radius.sm,
        objectFit: "cover",
        flex: "0 0 auto",
      }}
      data-testid="reply-thumb"
    />
  );
}

/**
 * Аватар автора сообщения (Антон, 2026-09-13).
 *
 * Раньше здесь были ТОЛЬКО инициалы — картинка не рисовалась вообще,
 * ни при каких условиях.
 *
 * Аватар берётся ИЗ КОМНАТЫ, а не из глобального профиля: в Matrix
 * участник может иметь своё имя и аватар в конкретной комнате, и именно
 * это показывают остальные клиенты.
 */
function SenderAvatar({
  senderId,
  senderName,
  roomId,
}: {
  senderId: string;
  senderName: string;
  roomId: string;
}) {
  const client = useMatrixClient();
  const mxc = client.getRoom(roomId)?.getMember(senderId)?.getMxcAvatarUrl() ?? null;
  const url = useAuthedAvatar(client, mxc);

  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: radius.circle,
        flex: "0 0 32px",
        display: "grid",
        placeItems: "center",
        background: url ? "transparent" : colorOf(senderId),
        color: color.textOnAccent,
        fontSize: font.small,
        fontWeight: font.weightBold,
        overflow: "hidden",
        userSelect: "none",
        marginBottom: 2,
      }}
      data-testid="msg-avatar"
      title={senderName}
    >
      {url ? (
        <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        senderName.replace(/^[#!@]/, "").slice(0, 2).toUpperCase()
      )}
    </div>
  );
}

/**
 * Permalink на событие — ссылка НА НАШ КЛИЕНТ, а не на matrix.to (патч
 * Антона 2026-09-10 10:31: «все должно открываться в нашем клиенте а не в
 * элементе»). Клиент умеет разбирать такой адрес: при открытии hash
 * #/room/<roomId>/<eventId> откроет комнату и проскроллит к событию.
 *
 * matrix.to при этом НИКУДА не исчезает из ОТПРАВЛЯЕМЫХ formatted_body:
 * там он требование спецификации (по нему упоминания понимают Element).
 * Здесь — только то, что мы отдаём в «Поделиться»/«Копировать ссылку».
 */
function permalinkOf(roomId: string, eventId: string): string {
  return appPermalink(roomId, eventId);
}

function dayOf(ts: number): string {
  return new Date(ts).toLocaleDateString(currentLocale(), {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/**
 * «1 ответ / 2 ответа / 5 ответов». Без склонения индикатор читался бы
 * как ломаный перевод, а это первое, что видно в ленте про тред.
 */
function pluralReplies(n: number, t: (k: TKey) => string): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return t("ui.otvetov");
  const mod10 = n % 10;
  if (mod10 === 1) return t("ui.otvet_2");
  if (mod10 >= 2 && mod10 <= 4) return t("ui.otveta");
  return t("ui.otvetov");
}

/* ------------------------------------------------------------------------
 * ССЫЛКИ в тексте сообщения.
 *
 * Зачем своя разметка, а не dangerouslySetInnerHTML из formatted_body:
 * formatted_body доверять нельзя (XSS), а санитайзера в проекте нет.
 * Поэтому body хранится как чистый текст, а ссылки выделяем здесь сами —
 * по явному http(s)://, без авто-линковки «www.» и голых доменов: ложное
 * срабатывание на «версия 2.0 вышла» хуже, чем пропущенный www.
 * --------------------------------------------------------------------- */

/** URL в тексте: только явный протокол http/https, до первого пробела. */
const URL_RE = /https?:\/\/[^\s<>"']+/gi;

/** Знаки препинания, которые человек ставит сразу после ссылки и которые
    в URL не входят: «смотри https://a.b/c!» — ссылка без восклицания. */
const TRAILING_PUNCT = /[.,!?:;)\]'»”]+$/;

/**
 * Email в тексте: локальная часть@домен. Не линкуем:
 *  - matrix-id (`@user:server`) — после @ идёт буква, а не точка;
 *  - адреса внутри URL — URL обрабатывается отдельно;
 *  - текст в кодовых блоках — у нас нет HTML, но текст в backticks
 *    не трогаем (требование приёмки).
 */
/** Raw Matrix user id in plain text, e.g. `@ab:algsuccess.com` (server part optional port). */
const RAW_MXID_RE = /@[a-z0-9._=\-\/+]+:[a-z0-9.\-]+(?::\d+)?/gi;
const EMAIL_RE = /(?<![\w@])[\w.+-]+@[\w-]+(?:\.[\w-]+)*\.[A-Za-z]{2,}(?![\w@])/g;

type RoomScrollState = {
  atBottom: boolean;
  /** Absolute fallback for a timeline whose anchor event is not loaded yet. */
  top: number;
  /** First event intersecting the viewport and its visual offset from the top. */
  anchorEventId: string | null;
  anchorOffset: number;
};
const roomScrollState = new Map<string, RoomScrollState>();

function captureRoomScrollState(el: HTMLDivElement, atBottom: boolean): RoomScrollState {
  const boxTop = el.getBoundingClientRect().top;
  let anchorEventId: string | null = null;
  let anchorOffset = 0;
  for (const node of el.querySelectorAll<HTMLElement>("[data-timeline-event-id]")) {
    const rect = node.getBoundingClientRect();
    if (rect.bottom > boxTop) {
      anchorEventId = node.dataset.timelineEventId ?? null;
      anchorOffset = rect.top - boxTop;
      break;
    }
  }
  return { atBottom, top: el.scrollTop, anchorEventId, anchorOffset };
}

/** Restore a message-relative viewport; return false while the anchor is not loaded. */
function restoreRoomScrollState(el: HTMLDivElement, state: RoomScrollState): boolean {
  if (state.atBottom) {
    el.scrollTop = el.scrollHeight;
    return true;
  }
  if (state.anchorEventId) {
    const boxTop = el.getBoundingClientRect().top;
    for (const node of el.querySelectorAll<HTMLElement>("[data-timeline-event-id]")) {
      if (node.dataset.timelineEventId !== state.anchorEventId) continue;
      el.scrollTop += node.getBoundingClientRect().top - boxTop - state.anchorOffset;
      return true;
    }
  }
  el.scrollTop = state.top;
  return state.anchorEventId === null;
}

/**
 * Превью ссылки: хук тянет /_matrix/media/v3/preview_url через client.
 * Одна карточка на сообщение (по первой ссылке). Если сервер вернул пусто
 * или ошибку — показываем просто ссылку, без пустой рамки.
 */
function useUrlPreview(client: MatrixClient, url: string | null) {
  const [preview, setPreview] = useState<{
    title: string | null;
    description: string | null;
    imageMxc: string | null;
    siteName: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!url) return;
    setLoading(true);
    setError(false);
    let cancelled = false;

    // preview_url требует url-encoded параметр. Второй аргумент ts — точка
    // во времени, относительно которой сервер берёт ближайший снапшот превью.
    client
      .getUrlPreview(url, Date.now())
      .then((data) => {
        if (cancelled) return;
        // Synapse отдаёт og:title, og:description, og:image, og:site_name.
        // og:image может быть mxc:// или http(s):// — берём только mxc,
        // иначе придётся качать с чужого сервера напрямую (утечка IP).
        const ogImage = data["og:image"] as string | undefined;
        const imageMxc = ogImage?.startsWith("mxc://") ? ogImage : null;
        setPreview({
          title: (data["og:title"] as string) ?? null,
          description: (data["og:description"] as string) ?? null,
          imageMxc,
          siteName: (data["og:site_name"] as string) ?? null,
        });
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client, url]);

  return { preview, loading, error };
}

/** Карточка превью ссылки под сообщением. */
function LinkPreview({ client, url }: { client: MatrixClient; url: string }) {
  const t = useT();
  const { preview, loading, error } = useUrlPreview(client, url);
  const { url: imageUrl } = useAuthedMedia(client, preview?.imageMxc ?? null);

  if (loading) {
    // Резервируем место, чтобы лента не прыгала при появлении карточки.
    return (
      <div
        style={{
          marginTop: space.sm,
          height: 72,
          borderRadius: radius.md,
          background: color.sidebarBg,
          border: `1px solid ${color.borderSoft}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: font.tiny,
          color: color.textDim,
        }}
        data-testid="link-preview-loading"
      >{t("timeline.loadingPreview")}</div>
    );
  }

  if (error || !preview || (!preview.title && !preview.description)) {
    return null; // просто ссылка, без пустой рамки
  }

  const domain = (() => {
    try {
      return new URL(url).hostname;
    } catch {
      return null;
    }
  })();

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      style={{
        display: "flex",
        gap: space.md,
        marginTop: space.sm,
        padding: space.md,
        borderRadius: radius.md,
        background: color.sidebarBg,
        border: `1px solid ${color.borderSoft}`,
        textDecoration: "none",
        color: "inherit",
        cursor: "pointer",
      }}
      data-testid="link-preview"
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          style={{
            width: 64,
            height: 64,
            borderRadius: radius.sm,
            objectFit: "cover",
            flex: "0 0 auto",
          }}
        />
      )}
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        {preview.title && (
          <div
            style={{
              fontWeight: font.weightBold,
              fontSize: font.small,
              color: color.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {preview.title}
          </div>
        )}
        {preview.description && (
          <div
            style={{
              fontSize: font.tiny,
              color: color.textDim,
              marginTop: 2,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {preview.description}
          </div>
        )}
        <div
          style={{
            fontSize: font.tiny,
            color: color.textMuted,
            marginTop: 4,
          }}
        >
          {preview.siteName ?? domain ?? url}
        </div>
      </div>
    </a>
  );
}

/**
 * Тело текстового сообщения со ссылками и кликабельными @упоминаниями.
 *
 * Ссылка — <a className="alg-msg-link"> (стили и :hover в timelineCss):
 * видна как ссылка (акцент, подчёркивание при наведении) и ломается в
 * любом месте (word-break: break-all), поэтому длинный URL без пробелов
 * не распирает пузырь по ширине. Открытие в новой вкладке, rel-защита
 * обязательна: target=_blank без noopener отдаёт открытой странице
 * window.opener нашего клиента.
 *
 * УПОМИНАНИЯ (Антон, 2026-09-14): @Имя из m.mentions рендерим ссылкой
 * на matrix.to — App перехватывает клик и открывает DM. Ищем только
 * те @Имя, которые сервер прислал в m.mentions, иначе любой «@» в
 * тексте стал бы ложной ссылкой.
 */
export function MessageBody({
  body,
  formattedBody,
  mentions,
  onMessagePrivately,
  onViewProfile,
}: {
  body: string;
  formattedBody?: string | null;
  mentions: { userId: string; displayName: string; self?: boolean }[] | null;
  onMessagePrivately?: (userId: string) => void;
  onViewProfile?: (userId: string) => void;
}) {
  const t = useT();
  // Собираем карту «@Имя» → userId. Ключ — displayName с префиксом @,
  // потому что в body упоминание выглядит как «@Антон», а не MXID.
  // Самоупоминание (self=true) в mentionMap НЕ кладём (Антон 2026-09-14):
  // ссылкой на DM с самим собой оно быть не должно — рендерится
  // подсвеченным текстом (ветка mention с clickable=false ниже).
  const mentionMap = new Map<string, string>();
  // «@Имя» самоупоминания — отдельная карта: ищем его в тексте так же,
  // как чужие, но помечаем clickable=false.
  const selfMentionMap = new Map<string, string>();
  if (mentions) {
    for (const m of mentions) {
      if (m.self) {
        selfMentionMap.set(`@${m.displayName}`, m.userId);
        continue;
      }
      // Metadata is still untrusted: only a syntactically valid Matrix user
      // id is allowed to reach the action menu.
      if (/^@[^\s:]+:[^\s:]+$/.test(m.userId)) {
        mentionMap.set(`@${m.displayName}`, m.userId);
      }
    }
  }

  const parts: Array<{ type: "text" | "link" | "email" | "mention"; text: string; userId?: string; display?: string; clickable?: boolean }> = [];
  let last = 0;
  const all: { index: number; length: number; type: "link" | "mention" | "email"; text: string; userId?: string; display?: string; clickable?: boolean }[] = [];
  // Raw Matrix IDs in the text (`@ab:algsuccess.com`) come from other clients
  // or from copy-paste. Anton (2026-09-17): never show the server part —
  // render them as `@ab` (or the room display name when known). Clickable
  // only when the server also listed the user in m.mentions; my own id
  // (self=true в `mentions`, 2026-10-09) stays a non-clickable pill.
  const knownNames = new Map<string, string>();
  if (mentions) for (const m of mentions) knownNames.set(m.userId, m.displayName);
  // Кто из упомянутых — я сам: их raw-MXID тоже остаётся НЕкликабельным
  // (Антон 2026-09-14), хотя knownNames их теперь знает (2026-10-09).
  const selfIds = new Set<string>();
  if (mentions) for (const m of mentions) if (m.self) selfIds.add(m.userId);
  const shortMxid = (id: string) => id.slice(0, id.indexOf(":"));
  const collectRawMxids = (value: string, into: typeof all) => {
    for (const match of value.matchAll(RAW_MXID_RE)) {
      const idx = match.index ?? 0;
      const id = match[0];
      const overlaps = into.some((a) => idx < a.index + a.length && idx + id.length > a.index);
      if (overlaps) continue;
      const name = knownNames.get(id);
      into.push({ index: idx, length: id.length, type: "mention", text: id, userId: id, display: name ? `@${name}` : shortMxid(id), clickable: knownNames.has(id) && !selfIds.has(id) });
    }
  };

  // Ссылки из текста.
  for (const match of body.matchAll(URL_RE)) {
    const idx = match.index ?? 0;
    let url = match[0];
    const trailing = url.match(TRAILING_PUNCT)?.[0] ?? "";
    if (trailing) url = url.slice(0, url.length - trailing.length);
    if (url) all.push({ index: idx, length: url.length, type: "link", text: url });
  }

  // Email — после URL: адрес внутри URL не должен стать почтой.
  for (const match of body.matchAll(EMAIL_RE)) {
    const idx = match.index ?? 0;
    // Не пересекается ли с уже найденной ссылкой?
    const overlaps = all.some(
      (a) => idx < a.index + a.length && idx + match[0].length > a.index,
    );
    if (!overlaps) {
      all.push({ index: idx, length: match[0].length, type: "email", text: match[0] });
    }
  }

  // Упоминания из m.mentions. Ищем только если есть подсказка от сервера,
  // иначе любой «@» в тексте станет ложной ссылкой.
  for (const [name, userId] of mentionMap) {
    let idx = body.indexOf(name);
    while (idx !== -1) {
      // Проверяем границу: «@Имя» не должно быть частью длинного слова.
      const before = idx === 0 ? " " : body[idx - 1];
      const after = idx + name.length >= body.length ? " " : body[idx + name.length];
      if (/[\s.,!?;:\n\r\t]/.test(before) && /[\s.,!?;:\n\r\t]/.test(after)) {
        all.push({ index: idx, length: name.length, type: "mention", text: name, userId });
      }
      idx = body.indexOf(name, idx + name.length);
    }
  }
  // Самоупоминание: тот же поиск «@Имя», но clickable=false — подсвеченный
  // текст, НЕ ссылка на DM с самим собой (Антон 2026-09-14 / 2026-10-09).
  for (const [name, userId] of selfMentionMap) {
    let idx = body.indexOf(name);
    while (idx !== -1) {
      const before = idx === 0 ? " " : body[idx - 1];
      const after = idx + name.length >= body.length ? " " : body[idx + name.length];
      if (/[\s.,!?;:\n\r\t]/.test(before) && /[\s.,!?;:\n\r\t]/.test(after)) {
        all.push({ index: idx, length: name.length, type: "mention", text: name, userId, clickable: false });
      }
      idx = body.indexOf(name, idx + name.length);
    }
  }
  collectRawMxids(body, all);

  all.sort((a, b) => a.index - b.index);

  // Отбрасываем пересекающиеся: упоминание внутри URL не обрабатываем.
  const filtered: typeof all = [];
  let end = 0;
  for (const a of all) {
    if (a.index >= end) {
      filtered.push(a);
      end = a.index + a.length;
    }
  }

  for (const a of filtered) {
    if (a.index > last) parts.push({ type: "text", text: body.slice(last, a.index) });
    parts.push({ type: a.type, text: a.text, userId: a.userId, display: a.display, clickable: a.clickable });
    last = a.index + a.length;
  }
  if (last < body.length) parts.push({ type: "text", text: body.slice(last) });

  const [mentionMenu, setMentionMenu] = useState<{ userId: string; x: number; y: number } | null>(null);

  // Parse Matrix HTML into React elements instead of injecting HTML. Only the
  // marks emitted by our composer are styled; unknown elements are transparent
  // and their text remains visible. Color is deliberately a closed palette.
  // ALG-631: значения больше не дублируют hex из темы литералами — иначе
  // в тёмной теме красный/зелёный/синий/фиолетовый текст падали бы ниже
  // WCAG-контраста. Токены var(--alg-composer*) сами подставляют пару
  // светлый/тёмный. Ключи объекта — закрытый набор ЗНАЧЕНИЙ data-mx-color
  // из roomActions.buildRichParts (отправляемый HTML не менялся).
  const safeColors: Record<string, string> = {
    "#d64545": color.composerRed,
    "#d97706": color.composerOrange,
    "#16845b": color.composerGreen,
    "#2878d0": color.composerBlue,
    "#8056c7": color.composerPurple,
  };
  const partsFor = (value: string) => {
    if (value === body) return parts;
    const found: typeof all = [];
    for (const match of value.matchAll(URL_RE)) {
      let text = match[0]; const trailing = text.match(TRAILING_PUNCT)?.[0] ?? "";
      if (trailing) text = text.slice(0, -trailing.length);
      if (text) found.push({ index: match.index ?? 0, length: text.length, type: "link", text });
    }
    for (const match of value.matchAll(EMAIL_RE)) {
      const index = match.index ?? 0;
      if (!found.some((a) => index < a.index + a.length && index + match[0].length > a.index)) found.push({ index, length: match[0].length, type: "email", text: match[0] });
    }
    for (const [name, userId] of mentionMap) {
      let index = value.indexOf(name);
      while (index !== -1) { found.push({ index, length: name.length, type: "mention", text: name, userId }); index = value.indexOf(name, index + name.length); }
    }
    for (const [name, userId] of selfMentionMap) {
      let index = value.indexOf(name);
      while (index !== -1) { found.push({ index, length: name.length, type: "mention", text: name, userId, clickable: false }); index = value.indexOf(name, index + name.length); }
    }
    collectRawMxids(value, found);
    found.sort((a, b) => a.index - b.index);
    const result: typeof parts = []; let lastIndex = 0;
    for (const a of found) { if (a.index < lastIndex) continue; if (a.index > lastIndex) result.push({ type: "text", text: value.slice(lastIndex, a.index) }); result.push({ type: a.type, text: a.text, userId: a.userId, display: a.display, clickable: a.clickable }); lastIndex = a.index + a.length; }
    if (lastIndex < value.length) result.push({ type: "text", text: value.slice(lastIndex) });
    return result;
  };
  const renderInline = (value: string) => (
    <>{partsFor(value).map((p, i) => {
      if (p.type === "link") return <a key={i} className="alg-msg-link" href={p.text} target="_blank" rel="noreferrer noopener" data-testid="msg-link">{p.text}</a>;
      if (p.type === "email") return <a key={i} className="alg-msg-link" href={`mailto:${p.text}`} data-testid="msg-email">{p.text}</a>;
      if (p.type === "mention" && p.userId && p.clickable === false) return (
        <span key={i} className="alg-msg-link" data-testid="mention-plain" data-user-id={p.userId}>{p.display ?? p.text}</span>
      );
      if (p.type === "mention" && p.userId) return (
        <button key={i} type="button" onClick={(event) => { event.stopPropagation(); const rect = event.currentTarget.getBoundingClientRect(); setMentionMenu({ userId: p.userId!, x: rect.left, y: rect.bottom + 4 }); }} className="alg-msg-link" data-testid="mention-link" data-user-id={p.userId} style={{ border: 0, padding: 0, background: "transparent", font: "inherit", cursor: "pointer" }}>{p.display ?? p.text}</button>
      );
      return <span key={i}>{p.text}</span>;
    })}</>
  );

  /** Plain messages still use familiar list syntax even without formatted_body. */
  const renderPlain = (value: string): ReactNode => {
    const lines = value.split("\n");
    const nodes: ReactNode[] = [];
    const orderedLine = /^\s*(\d+)[.)]\s+(.+)$/;
    const bulletLine = /^\s*[-*]\s+(.+)$/;
    let i = 0;

    while (i < lines.length) {
      const ordered = lines[i].match(orderedLine);
      const bullet = lines[i].match(bulletLine);
      if (ordered || bullet) {
        const kind = ordered ? "ol" : "ul";
        const items: ReactNode[] = [];
        const start = ordered ? Number(ordered[1]) : undefined;
        while (i < lines.length) {
          const match = kind === "ol" ? lines[i].match(orderedLine) : lines[i].match(bulletLine);
          if (!match) break;
          items.push(<li key={i} className="alg-msg-list-item">{renderInline(kind === "ol" ? match[2] : match[1])}</li>);
          i += 1;
        }
        nodes.push(kind === "ol"
          ? <ol key={`list-${i}`} className="alg-msg-list alg-msg-ordered-list" start={start}>{items}</ol>
          : <ul key={`list-${i}`} className="alg-msg-list alg-msg-bullet-list">{items}</ul>);
        continue;
      }

      const textLines: string[] = [];
      while (i < lines.length && !orderedLine.test(lines[i]) && !bulletLine.test(lines[i])) {
        textLines.push(lines[i]);
        i += 1;
      }
      if (textLines.length) nodes.push(<Fragment key={`text-${i}`}>{renderInline(textLines.join("\n"))}</Fragment>);
    }
    return <>{nodes}</>;
  };
  const renderFormatted = (html: string): ReactNode => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const visit = (node: Node, key: string): ReactNode => {
      if (node.nodeType === Node.TEXT_NODE) return renderInline(node.textContent ?? "");
      if (node.nodeType !== Node.ELEMENT_NODE) return null;
      const el = node as Element;
      const tag = el.tagName.toLowerCase();
      // WHITESPACE МЕЖДУ ПУНКТАМИ СПИСКА: HTML-клиенты (Element/markdown-it)
      // ставят переводы строк между </li> и <li>. Это разметка, а не
      // текст: реальный браузер такое между блочными <li> не показывает,
      // а renderPlain сделал бы из каждого \n отдельный <span> внутри
      // <ol> — мусорный инлайн-узел между пунктами. Пропускаем
      // whitespace-only текстовые узлы ТОЛЬКО у списков: в остальных
      // тегах переносы значимы (тело живёт с pre-wrap).
      const isListTag = tag === "ol" || tag === "ul";
      const children = Array.from(el.childNodes)
        .filter(
          (child) =>
            !(
              isListTag &&
              child.nodeType === Node.TEXT_NODE &&
              !(child.textContent ?? "").trim()
            ),
        )
        .map((child, i) => visit(child, `${key}-${i}`));
      if (tag === "strong" || tag === "b") return <strong key={key}>{children}</strong>;
      if (tag === "em" || tag === "i") return <em key={key}>{children}</em>;
      if (tag === "u") return <u key={key}>{children}</u>;
      if (tag === "del" || tag === "s" || tag === "strike") return <del key={key}>{children}</del>;
      // <br> и <p> раньше падали в Fragment ниже, то есть перенос строки
      // терялся совсем, а два абзаца слипались в одну строку. Для списков
      // это критично: markdown даёт «рыхлый» список вида <li><p>…</p></li>,
      // и без <p> пункт с двумя абзацами схлопывался в сплошной текст.
      // Отступы абзаца заданы классом (0 снаружи, воздух только между
      // соседними), чтобы пузырь не вырос на каждом сообщении.
      if (tag === "br") return <br key={key} />;
      if (tag === "p") return <p key={key} className="alg-msg-paragraph">{children}</p>;
      if (tag === "span") {
        const colorValue = safeColors[el.getAttribute("data-mx-color") ?? ""];
        return colorValue ? <span key={key} style={{ color: colorValue }}>{children}</span> : <Fragment key={key}>{children}</Fragment>;
      }
      // СПИСКИ (2026-09-16, «пропали маркеры нумерованных списков»):
      // раньше <ol>/<ul>/<li> падали в ветку Fragment ниже — дети
      // рисовались текстом без самой структуры, и нумерация/маркеры
      // исчезали. Теперь сохраняем и тег, и вложенность: <li> внутри
      // <ol>/<ul>, вложенные <ol>/<ul> внутри <li> рендерятся настоящими
      // элементами, и браузер сам считает номера (1,2,3… и ••).
      // Безопасность: из атрибутов читается РОВНО ОДИН — `start` у <ol>,
      // и только как целое число в разумных пределах (строка в React-props
      // не попадает никогда). Без него список, начатый с «3.», рисовался
      // с единицы — в Element такой список нумеруется с трёх (Антон,
      // 2026-09-18 п.4: эталон — Element Web).
      // type/reversed/class/style из чужого HTML по-прежнему отбрасываются.
      if (tag === "ol") {
        const raw = el.getAttribute("start");
        const parsed = raw !== null && /^-?\d{1,6}$/.test(raw.trim()) ? Number(raw.trim()) : NaN;
        const start = Number.isInteger(parsed) && parsed !== 1 ? parsed : undefined;
        return <ol key={key} className="alg-msg-list alg-msg-ordered-list" start={start}>{children}</ol>;
      }
      if (tag === "ul") return <ul key={key} className="alg-msg-list alg-msg-bullet-list">{children}</ul>;
      if (tag === "li") return <li key={key} className="alg-msg-list-item">{children}</li>;
      return <Fragment key={key}>{children}</Fragment>;
    };
    return Array.from(doc.body.childNodes).map((node, i) => visit(node, String(i)));
  };

  return (
    <>
      {(formattedBody ? renderFormatted(formattedBody) : renderPlain(body))}
      {/* Keep the menu outside parsed content so unsupported HTML can never create controls. */}
      {mentionMenu && (
        <div
          role="menu"
          style={{ position: "fixed", left: mentionMenu.x, top: mentionMenu.y, zIndex: 30, display: "flex", flexDirection: "column", gap: 2, padding: 4, borderRadius: radius.sm, border: `1px solid ${color.border}`, background: color.bg, boxShadow: "0 4px 16px rgba(0,0,0,.18)" }}
          onClick={(event) => event.stopPropagation()}
          data-testid="mention-menu"
        >
          <button type="button" role="menuitem" onClick={() => { onMessagePrivately?.(mentionMenu.userId); setMentionMenu(null); }} style={{ border: 0, background: "transparent", padding: "6px 10px", textAlign: "left", font: "inherit", cursor: "pointer", whiteSpace: "nowrap" }}>{t("chat.messagePrivately")}</button>
          <button type="button" role="menuitem" onClick={() => { onViewProfile?.(mentionMenu.userId); setMentionMenu(null); }} style={{ border: 0, background: "transparent", padding: "6px 10px", textAlign: "left", font: "inherit", cursor: "pointer", whiteSpace: "nowrap" }}>{t("profile.view")}</button>
        </div>
      )}
    </>
  );
}

/**
 * Разделитель дня — ОДИН компонент на все ветки ленты.
 *
 * Почему вынесен: после доводки Момо плашкой стоял только разделитель в
 * ветке обычных сообщений, а ветки звонка и системной строки всё ещё
 * рисовали старый серый микро-текст — один и тот же день оформлялся
 * по-разному в зависимости от того, какое событие его открыло.
 */
function DayDivider({ day }: { day: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "18px 0 8px" }}>
      <div
        style={{
          background: color.sidebarBg,
          border: `1px solid ${color.borderSoft}`,
          borderRadius: radius.pill,
          padding: "3px 12px",
          fontSize: font.tiny,
          fontWeight: font.weightBold,
          color: color.textDim,
          userSelect: "none",
        }}
        data-testid="day-divider"
      >
        {day}
      </div>
    </div>
  );
}

function formatAudioTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const whole = Math.floor(seconds);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const remainder = whole % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
    : `${minutes}:${String(remainder).padStart(2, "0")}`;
}

/**
 * Compact audio player for voice messages and regular audio attachments.
 *
 * The media element keeps the authenticated blob URL as its source. The
 * custom controls are deliberately ordinary buttons and a range input:
 * pointer/touch seeking is native, while Space/Enter and arrow keys work
 * without recreating browser accessibility behaviour in JavaScript.
 */
function AudioPlayer({
  url,
  name,
  onDownload,
  downloadBusy,
}: {
  url: string;
  name: string;
  onDownload: () => void;
  downloadBusy: boolean;
}) {
  const t = useT();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  const readDuration = useCallback(() => {
    const audio = audioRef.current;
    setDuration(audio && Number.isFinite(audio.duration) ? audio.duration : 0);
  }, []);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      void audio.play().catch(() => setPlaying(false));
    } else {
      audio.pause();
    }
  }, []);

  const progress = duration > 0 ? Math.min(100, (current / duration) * 100) : 0;
  const rangeStyle = { "--audio-progress": `${progress}%` } as CSSProperties;

  return (
    <div className="alg-audio-player" data-testid="audio-player">
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={readDuration}
        onDurationChange={readDuration}
        onTimeUpdate={(event) => setCurrent(event.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrent(0);
        }}
      />
      <button
        type="button"
        className="alg-audio-toggle"
        onClick={toggle}
        aria-label={`${t("notification.previewSound")}: ${name}`}
        aria-pressed={playing}
        title={t("notification.previewSound")}
        data-testid="audio-play-pause"
      >
        <span aria-hidden="true">{playing ? "❚❚" : "▶"}</span>
      </button>
      <div className="alg-audio-main">
        <input
          className="alg-audio-range"
          type="range"
          min={0}
          max={duration || 0}
          step={0.01}
          value={Math.min(current, duration || 0)}
          disabled={duration <= 0}
          onChange={(event) => {
            const next = Number(event.currentTarget.value);
            const audio = audioRef.current;
            if (audio) audio.currentTime = next;
            setCurrent(next);
          }}
          aria-label={name}
          aria-valuetext={`${formatAudioTime(current)} / ${formatAudioTime(duration)}`}
          style={rangeStyle}
          data-testid="audio-seek"
        />
        <div className="alg-audio-time" aria-hidden="true">
          <span>{formatAudioTime(current)}</span>
          <span>{formatAudioTime(duration)}</span>
        </div>
      </div>
      <button
        type="button"
        className="alg-audio-download"
        onClick={onDownload}
        disabled={downloadBusy}
        aria-label={t("timeline.downloadFile")}
        title={downloadBusy ? t("timeline.downloading") : t("timeline.downloadFile")}
        data-testid="audio-download"
      >
        <span aria-hidden="true">↓</span>
      </button>
    </div>
  );
}

/**
 * Вложение в ленте.
 *
 * ЧЕМ рисовать — по msgtype события:
 *  - image  → превью-картинка, клик открывает ЛАЙТБОКС (не новую вкладку —
 *    требование Антона 2026-09-10 10:15 «в том же фрейме»);
 *  - video  → маленький <video preload="metadata"> как постер, клик — тоже
 *    лайтбокс (само превью на паузе, играть — только в просмотрщике);
 *  - audio  → компактный плеер с полосой перемотки прямо в ленте;
 *    лайтбокс для короткого прослушивания не нужен;
 *  - file   → карточка с иконкой типа файла (pdf/doc/xls/zip/…), а не голая
 *    строка «📎 имя» (требование Антона 10:17). Клик — лайтбокс, где те же
 *    «Скачать»/«Поделиться», что и у картинок.
 *
 * Ограничение высоты превью обязательно: без него вертикальное фото занимает
 * всю ленту и пользователь теряет контекст разговора.
 */
export function Attachment({
  a,
  client,
  onOpen,
}: {
  a: NonNullable<TimelineItem["attachment"]>;
  client: MatrixClient;
  /** Открыть просмотрщик на этом вложении (только картинки и видео). */
  onOpen: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  // Загрузка с токеном в blob: — без этого наш Synapse отвечает 401 и картинка
  // выглядит битой (замер и причина — в шапке media.ts).
  const { url, error } = useAuthedMedia(client, a.mxc);
  const [busy, setBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // The authenticated media hook owns the Blob behind this object URL. Read it
  // locally, then let the service worker expose it once at a same-origin HTTPS
  // attachment URL. Electron must never be asked to navigate to blob:.
  const onDownload = useCallback(() => {
    if (!url || busy) return;
    setBusy(true);
    setDownloadError(null);
    // Обычный браузер (Opera GX и др., Антон 04.11 20:14): файл отдаётся
    // прямо из страницы через blob:, без service worker и новой вкладки.
    if (isPlainBrowser()) {
      void downloadInBrowser({ objectUrl: url, client, mxc: a.mxc, filename: a.name })
        .catch((downloadFailure: unknown) => setDownloadError(describeDownloadError(downloadFailure)))
        .finally(() => setBusy(false));
      return;
    }
    // Оболочки (Mac WKWebView, Android, Electron): прежний путь без изменений.
    void blobFromObjectUrl(url)
      .then((blob) => downloadBlobViaServiceWorker(blob, a.name))
      .catch((downloadFailure: unknown) => {
        setDownloadError(
          downloadFailure instanceof Error ? downloadFailure.message : "The attachment could not be downloaded.",
        );
      })
      .finally(() => setBusy(false));
  }, [url, busy, a.name, a.mxc, client]);

  // Ошибку ПОКАЗЫВАЕМ текстом, а не оставляем битую иконку: именно битая
  // иконка в первом прогоне чуть не прошла как «работает».
  if (!a.mxc || error) {
    return (
      <span data-testid="attachment-broken">
        {error
          ? tfmt("room.attachmentBroken", {
              name: a.name,
              size: humanSize(a.size) ? `· ${humanSize(a.size)}` : "",
              error,
            })
          : tfmt("room.attachmentBrokenNoError", {
              name: a.name,
              size: humanSize(a.size) ? `· ${humanSize(a.size)}` : "",
            })}
      </span>
    );
  }

  if (!url) {
    return (
      <span style={{ color: palette.textDim }} data-testid="attachment-loading">
        📎 {a.name}{t("timeline.loadingTail")}</span>
    );
  }

  if (a.msgtype === "m.image") {
    return (
      // maxWidth min(320px, 100%): фиксированные 320px на узком экране
      // (или при резерве padding-right: 60px под время) выпирали бы за
      // внутреннюю ширину пузыря. Ограничение по контейнеру обязательно.
      <div style={{ maxWidth: "min(320px, 100%)" }}>
        <button
          type="button"
          onClick={onOpen}
          data-testid="attachment-image"
          title={t("timeline.audioOpen")}
          style={{
            padding: 0,
            border: "none",
            background: "transparent",
            cursor: "zoom-in",
            display: "block",
            width: "100%",
          }}
        >
          <img
            src={url}
            alt={a.name}
            style={{
              maxWidth: "100%",
              maxHeight: 240,
              borderRadius: radius.md,
              display: "block",
            }}
          />
        </button>
        {(a.caption ?? a.name) && (
          <div
            style={{ fontSize: font.tiny, color: color.textDim, marginTop: space.xs }}
            data-testid="attachment-caption"
          >
            {a.caption ?? a.name}
          </div>
        )}
      </div>
    );
  }

  if (a.msgtype === "m.video") {
    // preload="metadata": кадр-постер и длительность есть, а сам файл не
    // тянется — автозагрузка содержимого в ленте остаётся запрещённой.
    return (
      <div style={{ maxWidth: "min(320px, 100%)", position: "relative" }}>
        <button
          type="button"
          onClick={onOpen}
          data-testid="attachment-video"
          title={t("timeline.audioOpen")}
          style={{
            padding: 0,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            display: "block",
            position: "relative",
            width: "100%",
          }}
        >
          <video
            src={url}
            preload="metadata"
            muted
            playsInline
            style={{
              maxWidth: "100%",
              maxHeight: 240,
              borderRadius: radius.md,
              display: "block",
            }}
          />
          <span
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              fontSize: 36,
              color: "#fff",
              textShadow: "0 1px 4px rgba(0,0,0,0.6)",
              pointerEvents: "none",
            }}
          >
            ▶
          </span>
        </button>
        {(a.caption ?? a.name) && (
          <div
            style={{ fontSize: font.tiny, color: color.textDim, marginTop: space.xs }}
            data-testid="attachment-caption"
          >
            {a.caption ?? a.name}
          </div>
        )}
      </div>
    );
  }

  if (a.msgtype === "m.audio") {
    // Голосовые и аудиофайлы играют прямо в ленте. Имя по-прежнему не
    // выводим отдельной подписью (у голосовых оно техническое), но оставляем
    // его доступным для assistive technology и имени скачиваемого файла.
    return (
      <div data-testid="attachment-audio">
        <AudioPlayer url={url} name={a.name} onDownload={onDownload} downloadBusy={busy} />
        {downloadError && (
          <div role="alert" style={{ color: color.danger, fontSize: font.tiny, marginTop: space.xs }}>
            {downloadError}
          </div>
        )}
      </div>
    );
  }

  // ДОКУМЕНТ (docx, pdf, архив…) — не медиа: показывать его в лайтбоксе
  // нечем, слайд встал бы пустым. Поэтому карточка СКАЧИВАЕТ файл, а не
  // открывает просмотрщик (Антон, 2026-09-10 11:21).
  return (
    <div style={{ maxWidth: "min(320px, 100%)" }}>
    <button
      type="button"
      onClick={onDownload}
      disabled={busy}
      data-testid="attachment-file"
      title={busy ? t("timeline.downloading") : t("timeline.downloadFile")}
      style={{
        font: "inherit",
        display: "flex",
        alignItems: "center",
        gap: space.md,
        // min(320px, 100%): карточка не выпирает за пузырь ни на узком
        // экране, ни из-за резерва padding-right: 60px под время.
        maxWidth: "min(320px, 100%)",
        width: "100%",
        boxSizing: "border-box",
        padding: `${space.md}px ${space.lg}px`,
        borderRadius: radius.md,
        border: `1px solid ${color.border}`,
        background: color.sidebarBg,
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span style={{ fontSize: 28, flex: "0 0 auto" }} data-testid="attachment-file-icon">
        {fileIconOf(a.name, a.mimetype)}
      </span>
      <span style={{ minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontSize: font.small,
            fontWeight: font.weightBold,
            color: palette.text,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {a.name}
        </span>
        <span style={{ display: "block", fontSize: font.tiny, color: palette.textDim }}>
          {busy
            ? t("timeline.downloading")
            : tfmt("timeline.attachmentDownload", {
                info:
                  humanSize(a.size) ||
                  a.mimetype ||
                  t("timeline.attachmentFallback"),
              })}
        </span>
      </span>
    </button>
    {a.caption && (
      <div
        style={{ fontSize: font.tiny, color: color.textDim, marginTop: space.xs }}
        data-testid="attachment-caption"
      >
        {a.caption}
      </div>
    )}
    {downloadError && (
      <div role="alert" style={{ color: color.danger, fontSize: font.tiny, marginTop: space.xs }}>
        {downloadError}
      </div>
    )}
    </div>
  );
}

export function Timeline({
  client,
  room,
  items,
  reactions,
  roomId,
  canLoadOlder,
  loadingOlder,
  onLoadOlder,
  onAtBottomChange,
  onUnreadReviewedThrough,
  scrollToBottomSignal,
  onMessagePrivately,
  onViewProfile,
  onReply,
  onEdit,
  onDelete,
  onReactionToggle,
  onOpenThread,
  onJumpToMessage,
  onJumpToMention,
  jumpToMentionBusy,
  callJoinState,
  onCallJoin,
  onCallDecline,
  onCallLeave,
}: {
  /** Нужен только для авторизованной загрузки вложений. */
  client: MatrixClient;
  room: Room;
  items: TimelineItem[];
  /** Агрегированные реакции: eventId -> чипсы. */
  reactions: Map<string, ReactionChip[]>;
  roomId: string;
  canLoadOlder: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  onAtBottomChange?: (atBottom: boolean) => void;
  /** Latest room-tail event after the whole unread range has been exposed. */
  onUnreadReviewedThrough?: (eventId: string | null) => void;
  /** Increments after this device successfully sends a new room message. */
  scrollToBottomSignal?: number;
  /** Actions for a validated m.mentions entry (never inferred from plain text). */
  onMessagePrivately?: (userId: string) => void;
  onViewProfile?: (userId: string) => void;
  /** Открыть композер в режиме ответа на сообщение. */
  onReply?: (eventId: string) => void;
  /** Открыть композер в режиме редактирования. */
  onEdit?: (eventId: string, currentBody: string) => void;
  /** Подтвердить удаление сообщения. */
  onDelete?: (eventId: string) => void;
  /** Переключить реакцию (добавить/снять). */
  onReactionToggle?: (eventId: string, emoji: string) => void;
  /** Открыть тред у сообщения. */
  onOpenThread?: (eventId: string) => void;
  /** Прокрутить к сообщению в ленте (для клика по цитате). */
  onJumpToMessage?: (eventId: string) => void;
  /** Кнопка «@»: есть непрочитанные упоминания (нажатие — к следующему). */
  onJumpToMention?: () => void;
  jumpToMentionBusy?: boolean;
  /**
   * Состояние звонка для карточки в ленте. Берём у родителя (RoomView):
   * useCallJoin — тяжёлый хук (LiveKit, членство, таймеры), и вызывать
   * его здесь вторым экземпляром значило бы иметь ДВА независимых входа
   * в звонок на одну комнату. Владелец хука один — RoomView.
   */
  callJoinState?: {
    state: "idle" | "joining" | "in-call" | "error";
    error: string | null;
  };
  onCallJoin?: () => void;
  /** Отказ от чужого звонка прямо из карточки (Антон 16:07). */
  onCallDecline?: () => void;
  onCallLeave?: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const stickToBottom = useRef(true);
  /** scrollHeight до вставки старых сообщений — для восстановления позиции. */
  const heightBefore = useRef<number | null>(null);
  const lastCount = useRef(0);
  /** Room restoration waits until its anchor event has actually rendered. */
  const restoring = useRef<RoomScrollState | null>(null);
  const renderedRoomId = useRef<string | null>(null);
  /** Последнее, что мы сказали родителю — чтобы не дёргать его одинаковым. */
  const reportedAtBottom = useRef<boolean | null>(null);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const handledScrollToBottomSignal = useRef(scrollToBottomSignal ?? 0);
  /** Открытое контекстное меню на сообщении. */
  const [menu, setMenu] = useState<{ eventId: string; x: number; y: number } | null>(null);
  /** Сообщение, для которого открыт диалог пересылки; null — закрыт.
      Живёт ЗДЕСЬ, а не в MessageMenu: меню размонтируется при выборе
      «Переслать», и состояние внутри него умерло бы вместе с диалогом. */
  const [forwardTarget, setForwardTarget] = useState<string | null>(null);
  /** Индекс открытого слайда лайтбокса в списке медиа комнаты, null — закрыт. */
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  /** Начало unread-диапазона должно попасть в окно после уже увиденного хвоста. */
  const unreadTailSeen = useRef(false);

  /**
   * Кто дочитал до какого сообщения (решение Антона 18:45 — аватары вместо
   * галочек). Карта eventId -> читатели, обновляется по Room.receipt.
   */
  const readers = useReadReceipts(client, room);

  /**
   * Кто под каким МОИМ сообщением стоит (постановка Антона 19:10:
   * «кто до какого сообщения просмотрел, кто остановился»).
   *
   * Каждый человек показывается ровно один раз — на своей позиции.
   * Дошли все до низа — аватары собрались внизу сами; кто-то отстал —
   * его лицо осталось там, где он стоит.
   */
  const readersAt = (() => {
    const me = client.getUserId();
    const mineIds: string[] = [];
    for (const it of items) {
      if (it.system !== null || it.call !== null) continue;
      if (it.senderId !== me) continue;
      if (it.eventId) mineIds.push(it.eventId);
    }
    return assignReaders(readers, mineIds);
  })();

  // Серверная (не синтетическая) граница прочитанного фиксирует диапазон,
  // который был непрочитан при входе. Если сама граница ещё не загружена,
  // подсвечиваем доступный хвост, но не отправляем receipt преждевременно.
  const meId = client.getUserId();
  const serverUnreadCount = room.getUnreadNotificationCount(NotificationCountType.Total) ?? 0;
  const readUpToEventId = meId ? room.getEventReadUpTo(meId, true) : null;
  const timelineEvents = room.getLiveTimeline().getEvents();
  const readTimelineIndex = readUpToEventId
    ? timelineEvents.findIndex((event) => event.getId() === readUpToEventId)
    : -1;
  const unreadBoundaryKnown = serverUnreadCount > 0 &&
    (readTimelineIndex >= 0 || (!readUpToEventId && !canLoadOlder));
  const unreadEventIds = new Set<string>();
  if (serverUnreadCount > 0) {
    const start = readTimelineIndex >= 0 ? readTimelineIndex + 1 : 0;
    for (const event of timelineEvents.slice(start)) {
      const id = event.getId();
      if (id) unreadEventIds.add(id);
    }
  }
  let receiptTargetEventId: string | null = null;
  for (let i = timelineEvents.length - 1; i >= 0; i--) {
    const event = timelineEvents[i];
    const id = event.getId();
    if (!id || id.startsWith("~") || room.hasPendingEvent(id)) continue;
    receiptTargetEventId = id;
    break;
  }

  /**
   * Все вложения комнаты в порядке ленты — содержимое лайтбокса. Стрелки
   * листают В ЕГО ПРЕДЕЛАХ (требование «в пределах комнаты»). Пересчёт на
   * каждый рендер дёшев: это один проход по items, которые и так только
   * что пересобраны.
   *
   * Медиа с битым mxc в список ВСЁ РАВНО входит: исключать его по
   * состоянию загрузки нельзя — индексы поплыли бы под пользователем прямо
   * во время листания. Лайтбокс сам честно покажет «не загрузилось».
   */
  // ПРОСМАТРИВАЕМОЕ и ДОКУМЕНТЫ — разные вещи (Антон, 2026-09-10 11:21).
  // В листалку попадают только картинки и видео: их осмысленно листать
  // стрелками. Документ (docx/pdf/архив) показывать в лайтбоксе нечем —
  // он бы встал пустым слайдом между двумя фото и сбил счётчик.
  // Аудио тоже вне листалки: оно играет прямо в ленте.
  const roomMedia: ViewerMedia[] = [];
  for (const m of items) {
    if (!m.attachment) continue;
    if (m.attachment.msgtype !== "m.image" && m.attachment.msgtype !== "m.video") continue;
    roomMedia.push({ ...m.attachment, eventId: m.eventId, ts: m.ts });
  }
  // ЯВНАЯ сортировка по времени: индекс 0 = самое СТАРОЕ, последний = самое
  // новое. Порядок items наследуется от live timeline и обычно уже такой, но
  // полагаться на это нельзя — пагинация и локальное эхо могут его нарушить,
  // а от порядка зависит направление стрелок (← старее, → новее).
  roomMedia.sort((a, b) => a.ts - b.ts);

  // Почему сброс только по roomId: раньше эффект перезапускался ещё на каждое
  // изменение readUpToEventId / serverUnreadCount / receiptTargetEventId — то
  // есть на каждый sync и на каждый локальный receipt. По порядку React этот
  // ПАССИВНЫЙ эффект бежит после useLayoutEffect'а, который как раз выставляет
  // reviewedThrough по факту прокрутки, — и мгновенно затирал его null'ом.
  // Обратного пересчёта layout-эффект при этом не делал (зависимости не
  // менялись), и спасали только повторные pin-таймеры. Если они уже
  // завершились — отметка прочтения не уходила НИКОГДА, хотя локальный
  // receipt в SDK уже стоял и счётчик в списке комнат гас. Это и есть
  // замеренное расхождение «циферка исчезает, а в комнате не прочитано»
  // (Антон, 2026-09-23 11:11 UTC).
  //
  // На самом деле сброс нужен только при СМЕНЕ КОМНАТЫ: внутри одной
  // комнаты любое изменение условий (новый хвост, пришедшее сообщение,
  // сдвинувшаяся граница) пересчитывается вызовами reportUnreadViewport из
  // layout-эффекта и onScroll — они сами поставят и null, и eventId.
  useEffect(() => {
    unreadTailSeen.current = false;
    onUnreadReviewedThrough?.(null);
  }, [roomId, onUnreadReviewedThrough]);

  const reportUnreadViewport = useCallback((el: HTMLDivElement) => {
    // Стенограмма условий отметки — под флагом algDebug, для живого замера
    // на стенде (задача Антона 2026-09-23). В обычной работе молчит.
    debugLog(
      `[receipt-trace] room=${roomId} unread=${serverUnreadCount} ` +
        `target=${receiptTargetEventId ?? "-"} readUpTo=${readUpToEventId ?? "-"} ` +
        `atBottom=${el.scrollHeight - el.scrollTop - el.clientHeight <= 2}`,
    );
    if (serverUnreadCount <= 0 || !receiptTargetEventId) {
      onUnreadReviewedThrough?.(null);
      return;
    }
    // Лента прокручена до конца = человек видит хвост комнаты. Этого достаточно,
    // чтобы отметить прочитанным, как делает Element. Раньше отметка требовала
    // ещё и известной границы непрочитанного (readUpToEventId в загруженном
    // таймлайне) и видимого ПЕРВОГО непрочитанного — при длинном хвосте или
    // отметке за пределами загруженной страницы условие не выполнялось никогда,
    // и открытый чат висел с бейджем «1» (Антон, 2026-09-18 11:30 UTC).
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 2) {
      onUnreadReviewedThrough?.(receiptTargetEventId);
      return;
    }
    if (!unreadBoundaryKnown) {
      onUnreadReviewedThrough?.(null);
      return;
    }
    const nodes = Array.from(el.querySelectorAll<HTMLElement>('[data-unread-event="1"]'));
    if (nodes.length === 0) {
      onUnreadReviewedThrough?.(receiptTargetEventId);
      return;
    }
    const box = el.getBoundingClientRect();
    const first = nodes[0].getBoundingClientRect();
    const last = nodes[nodes.length - 1].getBoundingClientRect();
    if (last.bottom > box.top && last.top < box.bottom) unreadTailSeen.current = true;
    const firstStartedVisible = first.top >= box.top && first.top < box.bottom;
    onUnreadReviewedThrough?.(
      unreadTailSeen.current && firstStartedVisible ? receiptTargetEventId : null,
    );
  }, [serverUnreadCount, receiptTargetEventId, unreadBoundaryKnown, readUpToEventId, roomId, onUnreadReviewedThrough]);

  /**
   * Один источник истины про «мы внизу», иначе прилипание и отметка
   * прочтения разошлись бы на первом же несовпадении порогов.
   * Only a tiny sub-pixel tolerance counts as the end. Treating the last
   * 80 px as "bottom" hid the jump control while the reader was still away
   * from the latest message. A short, non-scrollable timeline is still at end.
   */
  const measureAtBottom = useCallback((el: HTMLDivElement): boolean => {
    return el.scrollHeight - el.scrollTop - el.clientHeight <= 2;
  }, []);

  const publishAtBottom = useCallback(
    (atBottom: boolean) => {
      if (reportedAtBottom.current === atBottom) return;
      reportedAtBottom.current = atBottom;
      setShowJumpToBottom(!atBottom);
      onAtBottomChange?.(atBottom);
    },
    [onAtBottomChange],
  );

  const onScroll = useCallback(() => {
    const el = boxRef.current;
    if (!el) return;
    const atBottom = measureAtBottom(el);
    if (restoring.current) {
      // Setting the fallback scrollTop can emit scroll before the saved anchor
      // arrives. Do not let that transient viewport replace the saved room.
      stickToBottom.current = false;
      publishAtBottom(false);
    } else {
      stickToBottom.current = atBottom;
      roomScrollState.set(roomId, captureRoomScrollState(el, atBottom));
      publishAtBottom(atBottom);
    }
    reportUnreadViewport(el);

    // 200 px до верха — момент запросить страницу истории.
    if (el.scrollTop < 200 && canLoadOlder && !loadingOlder) {
      heightBefore.current = el.scrollHeight;
      debugLog(
        `[history] по прокрутке: scrollTop=${Math.round(el.scrollTop)} запрашиваю страницу`,
      );
      onLoadOlder();
    }
  }, [roomId, canLoadOlder, loadingOlder, onLoadOlder, measureAtBottom, publishAtBottom, reportUnreadViewport]);

  /**
   * ДОГРУЗКА, КОГДА ПРОКРУЧИВАТЬ НЕЧЕГО (Антон 18:52: «не понимаю, почему
   * история не подгружается»).
   *
   * Корень проблемы: единственным триггером был onScroll. Событие scroll
   * не возникает, если содержимое короче контейнера — полосы прокрутки нет,
   * крутить нечего, и история не грузится НИКОГДА. Ждать «прокрутите вверх»
   * в такой комнате бессмысленно: подсказка висит, а выполнить её нельзя.
   *
   * Почему это частый случай, а не экзотика: startClient берёт
   * initialSyncLimit=8, первая страница даёт 40 событий, а лента показывает
   * далеко не все — ответы в тредах отфильтрованы, серия state-событий
   * звонка схлопывается в одну карточку, членства/приглашения уходят в
   * тихие строки. 48 событий сервера легко превращаются в 3-5 видимых
   * элементов, которые не заполняют экран.
   *
   * Поэтому: пока контейнер НЕ прокручивается и история не исчерпана —
   * тянем следующую страницу. Ограничение на 6 страниц: защита от бесконечного
   * цикла в комнате, где сервер отдаёт только невидимые для ленты события.
   */
  const autoFillPages = useRef(0);
  /** Сколько видимых элементов было, когда мы просили предыдущую страницу. */
  const countAtRequest = useRef(0);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    if (!canLoadOlder || loadingOlder) return;

    const scrollable = el.scrollHeight > el.clientHeight + 4;
    const nearTop = el.scrollTop < 200;
    // Страница пришла, а видимых элементов не прибавилось.
    const grew = items.length > countAtRequest.current;

    // ВТОРАЯ ПРИЧИНА «иногда прокручивается, иногда нет» (Антон 18:57).
    // Человек доскроллил вверх, страница ушла и ВЕРНУЛАСЬ ПУСТОЙ для ленты:
    // сервер отдал 40 событий, но все они невидимые — ответы в тредах,
    // серия msc3401.call.member, членства. items не вырос, высота не
    // изменилась, прокрутка осталась на месте. А раз содержимое не выросло,
    // НОВОГО события scroll не будет: человек уже наверху и крутить дальше
    // некуда. Догрузка замирает до случайного касания колеса — отсюда
    // «периодически не прокручивается».
    const stalledAtTop = nearTop && !grew;

    if (scrollable && !stalledAtTop) {
      autoFillPages.current = 0;
      return;
    }
    if (autoFillPages.current >= 8) {
      debugLog(t("ui.history_avtodogruzka_ostanovlena_8_stra"));
      return;
    }
    autoFillPages.current += 1;
    countAtRequest.current = items.length;
    debugLog(
      `[history] ${stalledAtTop ? t("ui.naverhu_stranica_ne_dala_vidimyh_soobschen") : `лента не прокручивается (${el.scrollHeight}<=${el.clientHeight})`}` +
        `, тяну страницу ${autoFillPages.current}`,
    );
    onLoadOlder();
  }, [items, canLoadOlder, loadingOlder, onLoadOlder]);

  // Смена комнаты обнуляет счётчики: они про конкретную комнату.
  useEffect(() => {
    autoFillPages.current = 0;
    countAtRequest.current = 0;
  }, [roomId]);

  // Возврат из другого приложения/вкладки: если до ухода были внизу,
  // повторяем привязку после layout, но не вмешиваемся в чтение истории.
  useEffect(() => {
    const restoreIfNeeded = () => {
      if (document.visibilityState === "hidden") return;
      const state = roomScrollState.get(roomId);
      if (!state?.atBottom) return;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const el = boxRef.current;
        if (el && roomScrollState.get(roomId)?.atBottom) {
          el.scrollTop = el.scrollHeight;
          publishAtBottom(measureAtBottom(el));
        }
      }));
    };
    window.addEventListener("focus", restoreIfNeeded);
    document.addEventListener("visibilitychange", restoreIfNeeded);
    return () => {
      window.removeEventListener("focus", restoreIfNeeded);
      document.removeEventListener("visibilitychange", restoreIfNeeded);
    };
  }, [roomId, measureAtBottom, publishAtBottom]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      const state = restoring.current ?? roomScrollState.get(roomId);
      if (!state) return;
      if (state.atBottom && stickToBottom.current) {
        el.scrollTop = el.scrollHeight;
        publishAtBottom(measureAtBottom(el));
      } else if (!state.atBottom) {
        const restored = restoreRoomScrollState(el, state);
        if (restored) restoring.current = null;
        publishAtBottom(false);
      }
    });
    observer.observe(el);
    // The scroller has a fixed border-box; content/image changes do not resize
    // it, so observe event wrappers as well as the scroller itself.
    for (const node of el.querySelectorAll("[data-timeline-event-id]")) observer.observe(node);
    return () => observer.disconnect();
  }, [roomId, items.length, measureAtBottom, publishAtBottom]);

  // Pin a newly opened room while its initial events, images and pagination
  // settle. MutationObserver covers SDK updates that rerender without changing
  // the items array identity. A real wheel/pointer gesture cancels the pin so
  // the user can immediately start reading history.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    let active = true;
    let frame = 0;
    const pin = () => {
      if (!active) return;
      el.scrollTop = el.scrollHeight;
      stickToBottom.current = true;
      roomScrollState.set(roomId, {
        atBottom: true,
        top: el.scrollHeight,
        anchorEventId: null,
        anchorOffset: 0,
      });
      publishAtBottom(measureAtBottom(el));
      reportUnreadViewport(el);
    };
    const schedulePin = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(pin);
    };
    const stopPinning = () => { active = false; };
    const observer = new MutationObserver(schedulePin);
    observer.observe(el, { childList: true, subtree: true });
    el.addEventListener("wheel", stopPinning, { passive: true });
    el.addEventListener("pointerdown", stopPinning, { passive: true });
    const timers = [0, 50, 150, 400, 1000, 2500].map((delay) => window.setTimeout(schedulePin, delay));
    return () => {
      active = false;
      cancelAnimationFrame(frame);
      timers.forEach(window.clearTimeout);
      observer.disconnect();
      el.removeEventListener("wheel", stopPinning);
      el.removeEventListener("pointerdown", stopPinning);
    };
  }, [roomId, measureAtBottom, publishAtBottom, reportUnreadViewport]);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;

    if (renderedRoomId.current !== roomId) {
      renderedRoomId.current = roomId;
      // Решение Антона 16.09: каждый вход в чат начинается строго с хвоста.
      // Сохранённая позиция нужна внутри открытой комнаты (пагинация/resize),
      // но между комнатами произвольную точку больше не восстанавливаем.
      stickToBottom.current = true;
      restoring.current = null;
      heightBefore.current = null;
      lastCount.current = 0;
      reportedAtBottom.current = null;
      setViewerIndex(null);
      el.scrollTop = el.scrollHeight;
      roomScrollState.set(roomId, {
        atBottom: true,
        top: el.scrollHeight,
        anchorEventId: null,
        anchorOffset: 0,
      });

      // The first layout pass can still contain the previous room or an empty
      // timeline. Re-measuring it immediately used to overwrite the intended
      // bottom state with `false`. Repeat after the selected room is laid out.
      const frame = requestAnimationFrame(() => requestAnimationFrame(() => {
        const current = boxRef.current;
        if (!current || renderedRoomId.current !== roomId) return;
        current.scrollTop = current.scrollHeight;
        stickToBottom.current = true;
        roomScrollState.set(roomId, {
          atBottom: true,
          top: current.scrollHeight,
          anchorEventId: null,
          anchorOffset: 0,
        });
        publishAtBottom(measureAtBottom(current));
        reportUnreadViewport(current);
      }));
      lastCount.current = items.length;
      return () => cancelAnimationFrame(frame);
    }

    if (restoring.current) {
      if (restoreRoomScrollState(el, restoring.current)) restoring.current = null;
    } else if (heightBefore.current !== null) {
      // Догрузили историю: сдвигаем скролл на выросшую высоту, чтобы
      // пользователь остался на том же сообщении.
      const delta = el.scrollHeight - heightBefore.current;
      heightBefore.current = null;
      // Диагностика «почему прокрутка замерла»: delta<=0 значит, что
      // страница пришла, но видимых сообщений в ней не было.
      debugLog(
        `[history] после страницы: прирост высоты ${delta}px, элементов ${items.length} (было ${lastCount.current})`,
      );
      if (delta > 0) el.scrollTop += delta;
    } else if (stickToBottom.current && items.length !== lastCount.current) {
      el.scrollTop = el.scrollHeight;
    }
    lastCount.current = items.length;

    // Сообщаем после раскладки, а не в onScroll: открытие комнаты и приход
    // нового сообщения события scroll не генерируют, и без этого родитель
    // никогда бы не узнал, что лента уже внизу.
    if (restoring.current) {
      // Do not replace the saved viewport with scrollTop=0 while this room's
      // events are still arriving from sync/pagination.
      publishAtBottom(false);
    } else {
      const atBottom = measureAtBottom(el);
      publishAtBottom(atBottom);
      roomScrollState.set(roomId, captureRoomScrollState(el, atBottom));
    }
    reportUnreadViewport(el);
  }, [roomId, items.length, measureAtBottom, publishAtBottom, reportUnreadViewport]);

  // Sending our own message is an explicit intent to return to the live end,
  // even if the user had been reading history. Mark the room as sticky before
  // local echo arrives so that the subsequent item render remains at bottom.
  useLayoutEffect(() => {
    const signal = scrollToBottomSignal ?? 0;
    if (signal === handledScrollToBottomSignal.current) return;
    handledScrollToBottomSignal.current = signal;
    const el = boxRef.current;
    if (!el) return;
    restoring.current = null;
    stickToBottom.current = true;
    el.scrollTop = el.scrollHeight;
    roomScrollState.set(roomId, {
      atBottom: true,
      top: el.scrollHeight,
      anchorEventId: null,
      anchorOffset: 0,
    });
    publishAtBottom(true);
  }, [roomId, scrollToBottomSignal, publishAtBottom]);

  let prevDay = "";

  return (
    <div style={{ position: "relative", flex: "1 1 auto", minHeight: 0 }}>
    <div
      style={{ ...s.timeline, height: "100%", boxSizing: "border-box" }}
      ref={boxRef}
      onScroll={onScroll}
      data-testid="timeline"
    >
      {/* Стили пузырей: хвостик и время — псевдоэлементы, inline-стиль их
          не умеет. Одна статическая таблица на всю ленту.
          Рядом — timelineCss: :hover у пунктов контекстного меню и ссылок
          inline-стилем тоже не выразить. */}
      <style>{bubbleCss + timelineCss + readAvatarsCss}</style>
      {/* Шапка истории. КНОПКА, а не подсказка «прокрутите вверх»:
          подсказку невозможно выполнить, когда лента короче экрана и
          полосы прокрутки нет вообще (Антон 18:52 — «история не
          подгружается»). Автодогрузка такие комнаты добирает сама, но
          явное действие человеку нужно: 6 страниц подряд из невидимых
          событий автодогрузка останавливает, и дальше решает человек. */}
      {canLoadOlder && (
        <div style={{ textAlign: "center", padding: 8 }}>
          <button
            type="button"
            onClick={() => {
              const el = boxRef.current;
              if (el) heightBefore.current = el.scrollHeight;
              autoFillPages.current = 0;
              onLoadOlder();
            }}
            disabled={loadingOlder}
            data-testid="load-older"
            style={{
              font: "inherit",
              fontSize: font.small,
              padding: "4px 12px",
              borderRadius: radius.pill,
              border: `1px solid ${color.border}`,
              background: color.bg,
              color: loadingOlder ? color.textMuted : color.textDim,
              cursor: loadingOlder ? "default" : "pointer",
            }}
          >
            {loadingOlder ? t("room.historyLoading") : t("timeline.loadOlder")}
          </button>
        </div>
      )}
      {!canLoadOlder && items.length > 0 && (
        <div style={{ textAlign: "center", padding: 8, fontSize: 12, color: palette.textDim }}>{t("common.roomStart")}</div>
      )}

      {items.length === 0 && (
        <div style={{ ...s.centered, height: "auto", padding: 40 }}>{t("common.noMessages")}</div>
      )}

      {items.map((m, idx) => {
        const day = dayOf(m.ts);
        const newDay = day !== prevDay;
        prevDay = day;
        const chips = reactions.get(m.eventId) ?? [];
        const isUnreadEvent = unreadEventIds.has(m.eventId) && m.senderId !== meId;

        // Карточка звонка (MSC3401, Антон 2026-09-11 13:03). Элемент ленты
        // в хронологическом месте, а не плашка над ней: активный звонок
        // лежит у нижнего края (у последних сообщений), завершённый —
        // остаётся в истории записью «был звонок · участники · длительность».
        // Без аватара и пузыря, но КАРТОЧКОЙ, а не приглушённой строкой:
        // у активного звонка есть действие (войти), у завершённого —
        // сводка, и оба обязаны читаться как объект, а не как сноска.
        if (m.call) {
          return (
            <div key={m.eventId} data-timeline-event-id={m.eventId} data-unread-event={isUnreadEvent ? "1" : undefined}>
              {newDay && <DayDivider day={day} />}
              <CallCard
                call={m.call}
                ts={m.ts}
                joinState={callJoinState}
                onJoin={onCallJoin}
                onDecline={onCallDecline}
                onLeave={onCallLeave}
              />
            </div>
          );
        }

        // Системная строка: по центру, ТИХО (мелкий приглушённый текст),
        // без аватара, пузыря, контекстного меню и реакций (Антон, патч
        // 2026-09-10 10:34). Это служебная информация о комнате, а не
        // сообщение — и по весу она обязана уступать любому сообщению.
        if (m.system) {
          return (
            <div key={m.eventId} data-timeline-event-id={m.eventId} data-unread-event={isUnreadEvent ? "1" : undefined}>
              {newDay && <DayDivider day={day} />}
              <div
                style={{
                  textAlign: "center",
                  margin: "4px 0",
                  // font.tiny + textMuted: раньше строка была font.small
                  // и textDim — на глаз почти как подпись сообщения и
                  // спорила с лентой по весу.
                  fontSize: font.tiny,
                  color: color.textMuted,
                  userSelect: "none",
                }}
                data-testid="system-event"
                data-system-kind={m.system.kind}
                title={timeOf(m.ts)}
              >
                {m.system.text}
              </div>
            </div>
          );
        }

        // --- Пузырь сообщения (STEP8 эт.2) -----------------------------------
        // Свои — справа (bubbleOut, градиент), чужие — слева (bubbleIn).
        // Оба обязаны отличаться от ленты feedBg (постановка Антона 16:21).
        const isMine = m.senderId === client.getUserId();
        // Аватар и имя — только у первого сообщения в группе (startsGroup).
        // У остальных — прижаты к предыдущему, без отступа сверху.
        const showHead = m.startsGroup || newDay;
        // Хвостик — у последнего в группе. Определяем по следующему элементу:
        // если следующий не существует, или другой день, или другой автор,
        // или системное/звонок — значит, этот последний.
        const nextItem = items[idx + 1];
        const isLastInGroup =
          !nextItem ||
          nextItem.system !== null ||
          nextItem.call !== null ||
          nextItem.sender !== m.sender ||
          nextItem.ts - m.ts > 5 * 60 * 1000;
        // ПОЗИЦИЯ В ГРУППЕ целиком, а не только «последний». В Telegram
        // форма пузыря различается у первого, среднего и последнего: без
        // этого группа выглядит набором одинаковых плашек, и слипание
        // сообщений одного автора не читается.
        const groupPos = showHead
          ? isLastInGroup
            ? "solo"
            : "first"
          : isLastInGroup
            ? "last"
            : "mid";

        return (
          <div
            key={m.eventId}
            data-timeline-event-id={m.eventId}
            data-unread-event={isUnreadEvent ? "1" : undefined}
            className={isUnreadEvent && !isMine ? "alg-unread-message" : undefined}
          >
            {newDay && <DayDivider day={day} />}
            {/* Имя автора — только у первого в группе (как в Telegram).
                У своих сообщений имя не показываем вообще — там «Вы».
                Время из шапки группы перенесено ВНУТРЬ пузыря (требование
                этапа 2). */}
            {/* РАССТОЯНИЕ МЕЖДУ СООБЩЕНИЯМИ — здесь, одним местом.
                Внутри группы 2px: пузыри почти касаются, как в Telegram.
                Между группами 10px: видно, что сменился автор. Раньше
                отступ шёл и здесь, и в bubbleRow — в сумме группа
                разъезжалась и слипания не было. */}
            <div style={{ height: showHead ? 10 : 2 }} />
            {showHead && !isMine && (
              <div
                style={{
                  fontSize: font.small,
                  fontWeight: font.weightBold,
                  // Имя автора — цветом его аватара, как в Telegram: в
                  // группе взгляд различает авторов по цвету быстрее, чем
                  // по чтению имени.
                  color: colorOf(m.senderId),
                  marginBottom: 2,
                  // Сдвиг под аватар (32) + зазор (8): имя стоит над своим
                  // пузырём, а не над аватаром.
                  marginLeft: 40,
                }}
              >
                {m.senderName}
              </div>
            )}
            <div
              style={{
                display: "flex",
                flexDirection: isMine ? "row-reverse" : "row",
                alignItems: "flex-end",
                gap: space.md,
              }}
            >
              {/* Аватар — у ПОСЛЕДНЕГО сообщения группы и только у чужих.
                  Раньше стоял у первого (showHead) — визуально «висел» наверху
                  группы, а Telegram/WhatsApp/Element прижимают его к нижней
                  границе, рядом с самым свежим сообщением (Антон, 2026-09-14).
                  У своих — пустое место для выравнивания. */}
              {!isMine && isLastInGroup ? (
                <SenderAvatar senderId={m.senderId} senderName={m.senderName} roomId={roomId} />
              ) : (
                !isMine && <div style={{ width: 32, flex: "0 0 32px" }} />
              )}
              {/* Пузырь. */}
              <div
                style={{
                  ...s.bubbleRow,
                  maxWidth: "min(420px, 78%)",
                  alignSelf: isMine ? "flex-end" : "flex-start",
                }}
              >
                <div
                  // Форма, фон, хвостик и место под время — в bubbleCss:
                  // хвостик и часы это псевдоэлементы, inline их не умеет.
                  // Здесь остаётся только то, что зависит от данных.
                  className={[
                    "alg-bubble",
                    isMine ? "out" : "in",
                    groupPos === "solo" ? "last" : groupPos,
                    groupPos === "solo" ? "first" : "",
                    m.mentionsMe ? "mentions-me" : "",
                    // Надгробие удалённого сообщения: класс ДОБАВЛЯЕТСЯ к in/out,
                    // а не заменяет их — сторона ленты и время обязаны сохраниться
                    // (постановка Антона 2026-09-21 09:29, п. 4).
                    m.redacted ? "tombstone" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={{
                    // Отправляется — приглушаем целиком, это состояние
                    // сообщения, а не оформление.
                    // СТАТУС ОТПРАВКИ (постановка Антона 18:58):
                    // «отосланное идёт затемнённое, полупрозрачное; как
                    // только доставленное — становится полноценным; как
                    // только прочитанное — появился логотип прочитавшего».
                    // Прозрачность = «ещё не у сервера». Полная яркость =
                    // доставлено. Прочтение показывают аватары ниже.
                    opacity: m.pending ? 0.45 : 1,
                    transition: "opacity 0.25s ease-out",
                  }}
                  data-testid="message"
                  data-event-id={m.eventId}
                  data-mentions-me={m.mentionsMe ? "1" : "0"}
                  data-redacted={m.redacted ? "1" : "0"}
                  data-sender-id={m.senderId}
                  data-bubble={isMine ? "out" : "in"}
                  data-last-in-group={isLastInGroup ? "1" : "0"}
                  data-group-pos={groupPos}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setMenu({ eventId: m.eventId, x: e.clientX, y: e.clientY });
                  }}
                >
                  {/* Цитата реплая: внутри пузыря, над телом сообщения.
                      Полоска акцентом слева, имя автора, одна строка цитаты.
                      Клик = прыжок к оригиналу (onJumpToMessage).
                      Миниатюра картинки/видео — слева от текста (Антон 2026-09-14 10:00). */}
                  {m.replyTo && (
                    <div
                      style={{
                        borderLeft: `3px solid ${color.accent}`,
                        paddingLeft: space.md,
                        marginBottom: space.sm,
                        fontSize: font.small,
                        color: color.textDim,
                        cursor: m.replyTo.found && onJumpToMessage ? "pointer" : "default",
                        display: "flex",
                        alignItems: "center",
                        gap: space.md,
                      }}
                      onClick={() => {
                        if (m.replyTo?.found && onJumpToMessage) {
                          onJumpToMessage(m.replyTo.eventId);
                        }
                      }}
                      data-testid="reply-quote"
                      data-reply-to={m.replyTo.eventId}
                    >
                      {/* Миниатюра: только если оригинал — картинка/видео и mxc есть.
                          object-fit: cover, фиксированный размер — строка не прыгает. */}
                      {m.replyTo.found && m.replyTo.thumbMxc && (
                        <ReplyThumb client={client} mxc={m.replyTo.thumbMxc} />
                      )}
                      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                        <div style={{ fontWeight: font.weightBold, color: color.accent }}>
                          {m.replyTo.found ? m.replyTo.senderName : t("ui.soobschenie_ne_zagruzheno")}
                        </div>
                        <div
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 360,
                          }}
                        >
                          {m.replyTo.found ? m.replyTo.snippet : m.replyTo.snippet || "…"}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Тело сообщения. overflowWrap: anywhere дополняет
                      wordBreak из s.msgBody: длинное слово без пробелов
                      (URL, хеш, id) ломается в любом месте и не распирает
                      пузырь по ширине — иначе один такой токен растягивал
                      пузырь за пределы maxWidth ленты. */}
                  <div
                    style={{
                      ...s.msgBody,
                      overflowWrap: "anywhere",
                      minWidth: 0,
                      // Надгробие — тоже курсив. Важно именно здесь, а не только
                      // в .alg-bubble.tombstone: inline-стиль на ДОЧЕРНЕМ элементе бьёт
                      // наследование от класса родителя, и `normal` стёр бы курсив CSS.
                      fontStyle: m.emote || m.redacted ? "italic" : "normal",
                      color: m.failed ? palette.danger : undefined,
                    }}
                  >
                    {/* НАДГРОБИЕ удалённого сообщения — ПЕРВОЙ веткой.
                        У redacted-события content пуст (замер: `getContent() = {}`),
                        поэтому вложение/тело/превью строить не из чего и не нужно.
                        Текст берётся через t() здесь, а не из item.body: тогда смена
                        языка перерисовывает все надгробия сразу, без пересборки ленты.
                        <MessageBody> НЕ используем намеренно: он ищет ссылки,
                        пилюли и упоминания — в служебной фразе их быть не может. */}
                    {m.redacted ? (
                      <span data-testid="msg-tombstone">{t("message.wasDeleted")}</span>
                    ) : m.attachment ? (
                      <Attachment
                        a={m.attachment}
                        client={client}
                        onOpen={() => {
                          const idx = roomMedia.findIndex((v) => v.eventId === m.eventId);
                          if (idx >= 0) setViewerIndex(idx);
                        }}
                      />
                    ) : m.emote ? (
                      <>
                        {"* "}
                        {m.senderName}
                        {" "}
                        <MessageBody body={m.body} formattedBody={m.formattedBody} mentions={m.mentions} onMessagePrivately={onMessagePrivately} onViewProfile={onViewProfile} />
                      </>
                    ) : (
                      <>
                        <MessageBody body={m.body} formattedBody={m.formattedBody} mentions={m.mentions} onMessagePrivately={onMessagePrivately} onViewProfile={onViewProfile} />
                        {/* Превью первой ссылки в сообщении (Антон 2026-09-14 11:28).
                            Одна карточка на сообщение, даже если ссылок несколько. */}
                        {(() => {
                          const match = m.body.match(URL_RE);
                          if (!match) return null;
                          let url = match[0];
                          const trailing = url.match(TRAILING_PUNCT)?.[0] ?? "";
                          if (trailing) url = url.slice(0, url.length - trailing.length);
                          return url ? <LinkPreview client={client} url={url} /> : null;
                        })()}
                      </>
                    )}
                    {m.edited && (
                      <span style={{ fontSize: font.tiny, color: palette.textDim }}>{t("common.messageEdited")}</span>
                    )}
                    {/* Текста «отправляю…» нет намеренно: состояние
                        уже показано прозрачностью пузыря (постановка
                        Антона 18:58), а подпись дублировала бы его и
                        распирала пузырь по ширине. */}
                    {m.failed && <span style={{ fontSize: font.tiny }}>{t("common.messageNotSent")}</span>}
                  </div>
                  {/* Время внутри пузыря — справа снизу. */}
                  <span className="alg-bubble-time" data-testid="msg-time">
                    {timeOf(m.ts)}
                  </span>
                </div>
                {/* Реакции под пузырём.
                    alignSelf по стороне автора: свои — к правому краю
                    пузыря, чужие — к левому. Раньше ряд растягивался на
                    всю ширину колонки (flex + justifyContent), и при
                    широком ряде чипсов они уползали за край пузыря.
                    Размеры чипсов одинаковые: фиксированный line-height,
                    одинаковые отступы — ряд не пляшет по высоте от эмодзи
                    к эмодзи. */}
                {chips.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: space.sm,
                      marginTop: space.sm,
                      alignSelf: isMine ? "flex-end" : "flex-start",
                      maxWidth: "100%",
                    }}
                    data-testid="reactions"
                  >
                    {chips.map((chip) => (
                      <button
                        key={chip.key}
                        type="button"
                        style={{
                          font: "inherit",
                          fontSize: font.small,
                          lineHeight: 1.3,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: space.sm,
                          padding: "2px 8px",
                          borderRadius: radius.pill,
                          border: `1px solid ${chip.mine ? color.accent : color.border}`,
                          // Своя реакция — мягкий акцентный фон: видно,
                          // что повторный клик её снимет.
                          background: chip.mine ? color.accentSoft : color.bg,
                          color: chip.mine ? color.accent : color.text,
                          cursor: "pointer",
                        }}
                        onClick={() => onReactionToggle?.(m.eventId, chip.key)}
                        data-testid="reaction-chip"
                        data-reaction-key={chip.key}
                        data-mine={chip.mine ? "1" : "0"}
                      >
                        {chip.key} {chip.count}
                      </button>
                    ))}
                  </div>
                )}
                {/* КТО ПРОЧИТАЛ — маленькие аватары под пузырём
                    (решение Антона 18:45 вместо галочек).
                    Только у СВОИХ сообщений: «кто прочитал чужое» — не та
                    информация, которую человек ищет в ленте, и она бы
                    удваивала шум под каждым сообщением.
                    Только у ПОСЛЕДНЕГО своего с читателями: в Telegram
                    отметка стоит на хвосте, а не под каждой строкой —
                    иначе аватары дублируются на всю группу. */}
                {isMine && (readersAt.get(m.eventId)?.length ?? 0) > 0 && (
                  <div
                    className="alg-readers"
                    style={{ alignSelf: "flex-end" }}
                    data-testid="read-avatars"
                    data-count={readersAt.get(m.eventId)!.length}
                  >
                    {readersAt
                      .get(m.eventId)!
                      .slice(0, 5)
                      .map((r) => (
                        <ReaderAvatar
                          key={r.userId}
                          client={client}
                          userId={r.userId}
                          name={r.name}
                          initials={r.initials}
                          avatarMxc={r.avatarMxc}
                        />
                      ))}
                    {readersAt.get(m.eventId)!.length > 5 && (
                      <span className="alg-readers-more">
                        +{readersAt.get(m.eventId)!.length - 5}
                      </span>
                    )}
                  </div>
                )}
                {/* Индикатор треда. */}
                {m.thread && (
                  <button
                    type="button"
                    onClick={() => onOpenThread?.(m.eventId)}
                    data-testid="thread-indicator"
                    data-unread={m.thread.unread ? "1" : "0"}
                    style={{
                      font: "inherit",
                      fontSize: font.small,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: space.sm,
                      marginTop: space.sm,
                      padding: "3px 10px",
                      borderRadius: radius.pill,
                      border: `1px solid ${color.border}`,
                      background: m.thread.unread ? color.accentSoft : color.bg,
                      color: m.thread.unread ? color.accent : palette.textDim,
                      cursor: "pointer",
                      alignSelf: isMine ? "flex-end" : "flex-start",
                    }}
                  >
                    {m.thread.unread && (
                      <span
                        data-testid="thread-unread-dot"
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: color.accent,
                          flex: "0 0 auto",
                        }}
                      />
                    )}
                    🧵 {m.thread.count} {pluralReplies(m.thread.count, t)}
                    {m.thread.lastSenderName &&
                      tfmt("timeline.threadLastSender", {
                        name: m.thread.lastSenderName,
                      })}
                  </button>
                )}
              </div>
            </div>
            {/* Техническая информация — только под флагом отладки.
                id события, sender_id, timestamp — для разбора на стенде.
                В релизе лента должна быть чистой (постановка Антона 16:22). */}
            {DEBUG && (
              <div
                style={{
                  fontSize: font.micro,
                  color: color.textMuted,
                  fontFamily: font.mono,
                  marginTop: space.xs,
                  paddingLeft: isMine ? 0 : 40,
                  textAlign: isMine ? "right" : "left",
                }}
                data-testid="msg-debug"
              >
                {m.eventId} · {m.senderId} · {new Date(m.ts).toISOString()}
              </div>
            )}
          </div>
        );
      })}

      {menu && (
        <MessageMenu
          client={client}
          room={room}
          target={menu}
          items={items}
          onClose={() => setMenu(null)}
          onForward={(eventId) => setForwardTarget(eventId)}
          onReply={onReply}
          onEdit={onEdit}
          onDelete={onDelete}
          onReactionToggle={onReactionToggle}
          onOpenThread={onOpenThread}
          myReactions={
            new Set(
              (reactions.get(menu.eventId) ?? []).filter((c) => c.mine).map((c) => c.key),
            )
          }
        />
      )}

      {/* Диалог выбора получателя пересылки (Антон 05.10 12:37). Живёт на
          уровне ленты: меню, из которого его открыли, уже размонтировано.
          Сообщение ищем в items — за время жизни диалога лента могла
          обновиться, и свежий снимок честнее устаревшего. */}
      {forwardTarget && (() => {
        const forwardItem = items.find((i) => i.eventId === forwardTarget);
        return forwardItem ? (
          <ForwardDialog
            client={client}
            item={forwardItem}
            event={room.findEventById(forwardItem.eventId) ?? null}
            onClose={() => setForwardTarget(null)}
          />
        ) : null;
      })()}

      {/* Лайтбокс. Он position:absolute внутри s.main (position:relative):
          ограничен окном чата, список комнат остаётся видимым. Рендерим
          только когда открыт, чтобы размонтирование освобождало blob:-URL. */}
      {viewerIndex !== null && roomMedia[viewerIndex] && (
        <MediaViewer
          client={client}
          media={roomMedia}
          index={viewerIndex}
          onClose={() => setViewerIndex(null)}
          onNavigate={setViewerIndex}
        />
      )}
    </div>

    {/* The control must be outside the scrolling element. An absolutely
        positioned child of the scroller moves with its scrollable content
        and disappears precisely when the reader leaves the end. */}
    {onJumpToMention && (
      <button
        type="button"
        aria-label={t("chat.jumpToMention")}
        title={t("chat.jumpToMention")}
        data-testid="jump-to-mention"
        disabled={jumpToMentionBusy}
        onClick={onJumpToMention}
        style={{
          position: "absolute",
          right: "max(14px, env(safe-area-inset-right))",
          bottom: showJumpToBottom ? 66 : 14,
          width: 44,
          height: 44,
          display: "grid",
          placeItems: "center",
          borderRadius: radius.circle,
          border: `1px solid ${color.border}`,
          background: color.bg,
          color: color.accent,
          boxShadow: "0 4px 14px rgba(0,0,0,.24)",
          fontSize: 20,
          fontWeight: 700,
          lineHeight: 1,
          cursor: jumpToMentionBusy ? "progress" : "pointer",
          opacity: jumpToMentionBusy ? 0.6 : 1,
          zIndex: 20,
        }}
      >@</button>
    )}
    {showJumpToBottom && (
      <button
        type="button"
        aria-label={t("chat.jumpToLatest")}
        title={t("chat.jumpToLatest")}
        data-testid="jump-to-bottom"
        onClick={() => {
          const el = boxRef.current;
          if (!el) return;
          stickToBottom.current = true;
          roomScrollState.set(roomId, {
            atBottom: true,
            top: el.scrollHeight,
            anchorEventId: null,
            anchorOffset: 0,
          });
          el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
          requestAnimationFrame(() => publishAtBottom(measureAtBottom(el)));
        }}
        style={{
          position: "absolute",
          right: "max(14px, env(safe-area-inset-right))",
          bottom: 14,
          width: 44,
          height: 44,
          display: "grid",
          placeItems: "center",
          borderRadius: radius.circle,
          border: `1px solid ${color.border}`,
          background: color.bg,
          color: color.accent,
          boxShadow: "0 4px 14px rgba(0,0,0,.24)",
          fontSize: 22,
          lineHeight: 1,
          cursor: "pointer",
          zIndex: 20,
        }}
      >↓</button>
    )}
    </div>
  );
}

/**
 * Карточка звонка в ленте (MSC3401, Антон 2026-09-11 13:03).
 *
 * Живёт НА месте события в ленте, а не плашкой сверху: активный звонок
 * всегда у нижнего края (рядом с последними сообщениями — как в Slack и
 * Element), завершённый остаётся в истории записью «был звонок».
 *
 * Состояние входа берём из RoomView: useCallJoin — тяжёлый хук (LiveKit,
 * членство, таймеры), второй экземпляр на ту же комнату недопустим.
 * Кнопка входа — тот же клик, под который браузер отдаёт микрофон.
 */
/**
 * Похоже ли это на имя человека, а не на технический идентификатор.
 * Вторая линия обороны после исправления в timeline.ts: карточка звонка
 * в ленте не должна показывать служебные строки ни при каких данных.
 */
function isHumanName(name: string): boolean {
  if (!name) return false;
  if (name.startsWith("_") || name.startsWith("@")) return false;
  if (name.includes("_m.call") || name.includes(":")) return false;
  return name.length <= 64;
}

function CallCard({
  call,
  ts,
  joinState,
  onJoin,
  onLeave,
  onDecline,
}: {
  call: NonNullable<TimelineItem["call"]>;
  ts: number;
  joinState?: { state: "idle" | "joining" | "in-call" | "error"; error: string | null };
  onJoin?: () => void;
  onLeave?: () => void;
  /** Отказаться от чужого звонка прямо из карточки (Антон 16:07). */
  onDecline?: () => void;
}) {
  const t = useT();
  const state = joinState?.state ?? "idle";
  const participantsWord =
    call.participants === 1
      ? t("ui.uchastnik")
      : call.participants < 5
        ? t("ui.uchastnika")
        : t("ui.uchastnikov");

  return (
    <div
      style={{
        margin: "8px auto",
        maxWidth: 460,
        width: "fit-content",
        minWidth: 260,
        padding: "10px 16px",
        borderRadius: radius.md,
        background: call.active ? color.accentSoft : color.sidebarBg,
        border: `1px solid ${call.active ? color.accent : color.border}`,
        display: "flex",
        alignItems: "center",
        gap: space.lg,
        fontSize: font.small,
        color: color.text,
      }}
      data-testid="call-card"
      data-call-active={call.active ? "1" : "0"}
      data-call-state={state}
      title={timeOf(ts)}
    >
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          color: call.active ? color.accent : color.textDim,
        }}
      >
        {/* Активный звонок — акцентная трубка; завершённый — приглушённая.
            Эмодзи 📞/☎️ не умели менять цвет от состояния. */}
        <IconPhone size={18} />
      </span>
      <span style={{ flex: "1 1 auto", minWidth: 0 }}>
        {call.active ? (
          <>
            <b>{t("timeline.callOngoing")}</b>
            {call.names.filter(isHumanName).length > 0 && (
              <> · {call.names.filter(isHumanName).join(", ")}</>
            )}
            {/* «начался в HH:MM» убрано: время теперь стоит справа в
                карточке отдельной меткой, как время в пузыре сообщения.
                Два времени в одной карточке читаются как ошибка. */}
            <span style={{ display: "block", fontSize: font.tiny, color: color.textDim, marginTop: 2 }}>
              {call.participants} {participantsWord}
            </span>
          </>
        ) : (
          <>
            <b>{t("timeline.callFinished")}</b>
            {/* Имена показываем ТОЛЬКО если они человеческие. Страховка от
                повтора дефекта 12.09 10:36: если в подпись once again
                просочится технический идентификатор (state_key вида
                _@user_DEVICE_m.call), лучше не показать имён вовсе, чем
                залить ленту служебными строками. Признак — @ или _ в
                начале, либо подозрительно длинная строка. */}
            {call.names.filter(isHumanName).length > 0 && (
              <> · {call.names.filter(isHumanName).join(", ")}</>
            )}
            <span style={{ display: "block", fontSize: font.tiny, color: color.textDim, marginTop: 2 }}>
              {call.participants} {participantsWord}
              {call.endedTs !== null && call.endedTs > call.startedTs
                ? ` · ${formatCallDuration(call.endedTs - call.startedTs, t)}`
                : ""}
            </span>
          </>
        )}
      </span>
      {call.active && state === "idle" && onJoin && (
        <button
          type="button"
          // onClick={onJoin} передавал бы MouseEvent первым аргументом, а
          // join(withVideo) считал его truthy -> аудиовход включал камеру
          // (баг Антона 15:58). Вызываем без аргументов.
          onClick={() => onJoin?.()}
          data-testid="call-card-join"
          title={t("room.headerCallJoin")}
          aria-label={t("room.headerCallJoin")}
          /* ЗЕЛЁНАЯ ТРУБКА, без надписи (Антон 12.09 09:43: «принять везде
             и сделать зелёненькую... во всех этих сообщениях»). Было:
             красная кнопка рядом с красной «Отказаться» — «когда сейчас
             две красных, это очень путает». */
          style={{
            flex: "0 0 auto",
            font: "inherit",
            width: 32,
            height: 32,
            borderRadius: radius.circle,
            border: "none",
            background: color.ok,
            color: color.textOnAccent,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconPhone size={16} />
        </button>
      )}
      {/* «Отказаться» рядом с «Присоединиться» (Антон 16:07): у карточки
          звонка в ленте тоже должно быть два решения, а не одно. */}
      {call.active && state === "idle" && onDecline && (
        <button
          type="button"
          onClick={() => onDecline?.()}
          data-testid="call-card-decline"
          title={t("ui.otkazatsya_ot_zvonka_zvonyaschiy_uznaet_ob")}
          aria-label={t("ui.otkazatsya_ot_zvonka")}
          /* КРАСНАЯ ПЕРЕЧЁРКНУТАЯ ТРУБКА. Перечёркнутая, а не просто
             красная: цвет не должен быть единственным отличием от
             «принять» (дальтонизм, ч/б печать). */
          style={{
            flex: "0 0 auto",
            font: "inherit",
            width: 32,
            height: 32,
            borderRadius: radius.circle,
            border: "none",
            // Мои кнопки отказа стояли на литерале #c0392b — второй
            // источник правды мимо палитры. Токен danger (этап 1).
            background: color.danger,
            color: color.textOnAccent,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconPhoneOff size={16} />
        </button>
      )}
      {call.active && state === "joining" && (
        <span style={{ flex: "0 0 auto", fontSize: font.small, color: color.textDim }}>{t("room.callConnecting")}</span>
      )}
      {call.active && state === "in-call" && onLeave && (
        <button
          type="button"
          onClick={onLeave}
          data-testid="call-card-leave"
          title={t("room.headerCallLeave")}
          aria-label={t("room.headerCallLeave")}
          /* «Выйти» — тоже красная трубка: это завершение разговора, то же
             семейство действий, что и отказ. */
          style={{
            flex: "0 0 auto",
            font: "inherit",
            width: 32,
            height: 32,
            borderRadius: radius.circle,
            border: "none",
            background: color.danger,
            color: color.textOnAccent,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconPhoneOff size={16} />
        </button>
      )}
      {/* ВРЕМЯ ЗВОНКА в карточке (Антон 12.09 08:48: «добавить время в
          балон звонка»). Раньше время было только всплывающей подсказкой
          при наведении (title) — то есть на планшете недоступно вообще.
          Показываем так же, как время в пузыре сообщения: справа, мелко,
          приглушённо. Идёт звонок — время начала; завершён — начало и
          конец через тире, чтобы было видно окно разговора, а не только
          длительность.

          ТЕХНИЧЕСКАЯ ИНФОРМАЦИЯ УДАЛЕНА СОВСЕМ (Антон 12.09 08:48:
          «убрать техническую информацию о звонке из чата»). Раньше здесь
          под флагом отладки печатались ISO-метки startedTs/endedTs
          (data-testid="call-card-debug"). Постановка от 11.09 была
          «спрятать до конца отладки, потом убрать совсем» — это и есть
          «потом». Блок вырезан, а не спрятан глубже. */}
      <span
        style={{
          flex: "0 0 auto",
          alignSelf: "flex-end",
          fontSize: font.tiny,
          color: color.textDim,
          whiteSpace: "nowrap",
        }}
        data-testid="call-card-time"
      >
        {call.endedTs !== null && call.endedTs > call.startedTs
          ? `${timeOf(call.startedTs)} – ${timeOf(call.endedTs)}`
          : timeOf(call.startedTs)}
      </span>
    </div>
  );
}

/** «4 мин» / «1 ч 12 мин» — длительность звонка человеческими словами. */
function formatCallDuration(ms: number, t: (k: TKey) => string): string {
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 1) return t("ui.menshe_minuty");
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return tf("timeline.durationMinutes", { minutes: totalMin });
  if (m === 0) return tf("timeline.durationHours", { hours: h });
  return tf("timeline.durationHoursMinutes", { hours: h, minutes: m });
}

/** Контекстное меню сообщения (правый клик). */
/**
 * Быстрые реакции в контекстном меню. Шесть штук — ряд влезает в ширину
 * меню без переноса; больше требует пикера со поиском, это отдельная работа.
 */
const QUICK_REACTIONS = ["👍", "❤️", "😂", "🎉", "👀", "🙏"];

function MessageMenu({
  client,
  room,
  target,
  items,
  onClose,
  onForward,
  onReply,
  onEdit,
  onDelete,
  onReactionToggle,
  onOpenThread,
  myReactions,
}: {
  client: MatrixClient;
  room: Room;
  target: { eventId: string; x: number; y: number };
  items: TimelineItem[];
  onClose: () => void;
  /** Открыть диалог пересылки для этого события (диалог живёт на уровне
      ленты — меню к тому моменту уже размонтировано). */
  onForward?: (eventId: string) => void;
  onReply?: (eventId: string) => void;
  onEdit?: (eventId: string, currentBody: string) => void;
  onDelete?: (eventId: string) => void;
  onReactionToggle?: (eventId: string, emoji: string) => void;
  onOpenThread?: (eventId: string) => void;
  /** Эмодзи, которые Я уже поставил на это событие: подсвечиваем в ряду. */
  myReactions?: Set<string>;
}) {
  const t = useT();
  const tfmt = useTF();
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ left: target.x, top: target.y });
  /** Координаты открытого пикера эмодзи; null — закрыт. */
  const [pickerAt, setPickerAt] = useState<{ x: number; y: number } | null>(null);
  const item = items.find((i) => i.eventId === target.eventId);
  const me = client.getUserId() ?? "";

  // Находим исходное событие для проверки прав на удаление.
  const sourceEvent = room.getLiveTimeline().getEvents().find((ev) => ev.getId() === target.eventId);

  const isMine = item?.senderId === me;
  // НАДГРОБИЕ: удалённое сообщение осталось в ленте, но содержимого у него
  // нет: править, отвечать, цитировать, открывать тред и удалять повторно
  // нечего. Сервер второй redact просто проигнорирует, но живая кнопка
  // «Удалить» на надгробии обещает действие, которого не произойдёт.
  const isTomb = Boolean(item?.redacted);
  const canEdit = isMine && !item?.failed && !item?.pending && !isTomb;
  const canDelete = !isTomb && (sourceEvent ? canRedactEvent(client, room, sourceEvent) : false);
  // canCopy и canShareLink гаснут сами: у надгробия `body === ""` по построению
  // (см. tombstoneItemOf) — копировать пустоту нельзя.
  const canCopy = Boolean(item?.body && !item.attachment);
  // ПЕРЕСЛАТЬ (Антон 05.10 12:37; 15.10 — «пересылать можно любые типы
  // сообщений»). Предикат — ТОТ ЖЕ, что проверяет сама отправка
  // (canForwardMessage, matrix/forward.ts): два правила разошлись бы, и
  // пункт меню показывался бы там, где сервис откажет. Вложения, голосовые,
  // стикеры — пересылаются; нет пункта только у надгробия, эха и служебных.
  // Событие ищем и в тредах (findEventById), не только в живой ленте.
  const forwardEvent = sourceEvent ?? room.findEventById(target.eventId);
  const canForward = Boolean(item && canForwardMessage(item, forwardEvent));
  // «Поделиться» — системное меню устройства. Рисуем ТОЛЬКО там, где Web
  // Share API реально работает; иначе остаются «Копировать ссылку/текст».
  // Проверку кешируем на время жизни меню: она дёшевая, но синхронная, и
  // на каждый рендер дёргать её ни к чему.
  const [canShare] = useState<boolean>(() => canShareText());
  // Permalink строим только для серверных событий: у локального эха id
  // вида «~local…», и ссылка на него для получателя — мусор.
  const canShareLink = Boolean(
    item && !item.pending && !item.failed && target.eventId.startsWith("$"),
  );

  useEffect(() => {
    // Пока открыт пикер, меню НЕ закрываем: пикер рисуется вне DOM меню
    // (position:fixed), и любой клик по эмодзи иначе прилетал бы сюда как
    // «клик мимо» и рушил меню раньше выбора.
    if (pickerAt) return;
    const close = () => onClose();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const id = setTimeout(() => {
      window.addEventListener("mousedown", close);
      window.addEventListener("contextmenu", close);
      window.addEventListener("keydown", onKey);
    }, 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener("mousedown", close);
      window.removeEventListener("contextmenu", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, pickerAt]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left = Math.min(target.x, window.innerWidth - r.width - 8);
    const top = Math.min(target.y, window.innerHeight - r.height - 8);
    setPos({ left: Math.max(8, left), top: Math.max(8, top) });
  }, [target.x, target.y]);

  // Пункт меню — классом alg-menu-item (timelineCss), а не чистым
  // inline-стилем: :hover inline не выразить, а без наведения меню
  // выглядело мёртвым и спорило с палитрой (фон/граница/наведение).
  const menuItem = (label: string, testId: string, fn: () => void, danger = false, disabled = false) => (
    <button
      key={testId}
      type="button"
      className={`alg-menu-item${danger ? " danger" : ""}`}
      data-testid={testId}
      disabled={disabled}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={() => {
        if (disabled) return;
        fn();
        onClose();
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      ref={ref}
      // Фон, граница, радиус, тень и внутренние отступы — классом alg-menu
      // (timelineCss), чтобы наведение и палитра жили в одном месте.
      // Инлайном остаётся только то, что зависит от данных: позиция.
      className="alg-menu"
      style={{
        position: "fixed",
        left: pos.left,
        top: pos.top,
        padding: space.sm,
        minWidth: 200,
        zIndex: 60,
        display: "flex",
        flexDirection: "column",
      }}
      data-testid="message-menu"
      data-event-id={target.eventId}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Быстрые реакции РЯДОМ, а не отдельным подменю (Антон, 2026-09-10
          11:43). Набор фиксированный: полноценный пикер эмодзи — отдельная
          работа, а в 90% случаев нужны эти шесть. Повторный клик по своей
          реакции её СНИМАЕТ: handleReactionToggle уже умеет и redact, и
          отправку, второй ветки здесь не нужно.
          На НАДГРОБИИ ряда нет вовсе: реакция на удалённое сообщение —
          осиротевшее событие, которое лента тут же скроет (isOrphanedRelation),
          то есть клик выглядел бы как молча провалившееся действие. */}
      {onReactionToggle && !isTomb && (
        <div
          style={{
            display: "flex",
            gap: 2,
            padding: "4px 6px",
            borderBottom: `1px solid ${color.border}`,
          }}
          data-testid="msg-reactions-row"
        >
          {QUICK_REACTIONS.map((emoji) => {
            const mine = myReactions?.has(emoji) ?? false;
            return (
              <button
                key={emoji}
                type="button"
                title={
                  mine
                    ? t("ui.snyat_reakciyu")
                    : tfmt("timeline.addReaction", { emoji })
                }
                data-testid={`msg-react-${emoji}`}
                data-mine={mine ? "1" : "0"}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => {
                  onReactionToggle(target.eventId, emoji);
                  onClose();
                }}
                style={{
                  font: "inherit",
                  fontSize: 18,
                  lineHeight: 1,
                  padding: "4px 6px",
                  borderRadius: radius.sm,
                  cursor: "pointer",
                  // Своя реакция видна фоном: иначе непонятно, что клик снимет.
                  border: mine ? `1px solid ${color.accent}` : "1px solid transparent",
                  background: mine ? color.accentSoft : "transparent",
                }}
              >
                {emoji}
              </button>
            );
          })}
          {/* «…» — полный набор эмодзи устройства (Антон, 2026-09-10 11:57).
              Шести быстрых мало, но пикер грузится отдельным чанком и только
              по этому клику: тащить 300 КБ в основной бандл ради редкой
              кнопки нельзя. */}
          <button
            type="button"
            title={t("ui.drugoy_emodzi")}
            data-testid="msg-react-more"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
              setPickerAt({ x: r.left, y: r.bottom + 4 });
            }}
            style={{
              font: "inherit",
              fontSize: 18,
              lineHeight: 1,
              padding: "4px 8px",
              borderRadius: radius.sm,
              cursor: "pointer",
              border: "1px solid transparent",
              background: pickerAt ? color.accentSoft : "transparent",
              color: color.text,
            }}
          >
            …
          </button>
        </div>
      )}
      {pickerAt && onReactionToggle && (
        <EmojiPicker
          anchor={pickerAt}
          onClose={() => setPickerAt(null)}
          onPick={(ch) => {
            onReactionToggle(target.eventId, ch);
            setPickerAt(null);
            onClose();
          }}
        />
      )}
      {onReply && menuItem(t("timeline.reply"), "msg-reply", () => onReply(target.eventId), false, isTomb)}
      {/* Подпись зависит от того, есть ли уже ответы: «Тред» на сообщении
          без ответов создаёт новый, на сообщении с тредом — открывает
          существующий. Антон 10.09 проверил это поведение и оно верное;
          подпись делаем честной, чтобы не выглядело как повторное создание. */}
      {onOpenThread &&
        menuItem(
          item?.thread
            ? tfmt("timeline.openThreadCount", { count: item.thread.count })
            : t("ui.otvetit_v_trede"),
          "msg-open-thread",
          () => onOpenThread(target.eventId),
          false,
          isTomb,
        )}
      {onEdit && menuItem(t("timeline.edit"), "msg-edit", () => {
        if (item) onEdit(target.eventId, item.body);
      }, false, !canEdit)}
      {onForward && canForward &&
        menuItem(t("message.forward"), "msg-forward", () => {
          // Диалог открывается НА УРОВНЕ ЛЕНТЫ (forwardTarget в Timeline):
          // menuItem тут же вызовет onClose и размонтирует это меню, и
          // состояние внутри него умерло бы вместе с диалогом.
          onForward(target.eventId);
        })}
      {canShare && canShareLink && menuItem(t("media.share"), "msg-share", () => {
        // В share кладём И текст, И ссылку (требование: получатель видит
        // содержимое, а не голую ссылку). Для вложения body — имя файла,
        // оно лучше, чем ничего.
        const text = item?.body ? `${item.body}\n` : "";
        const body = `${text}${permalinkOf(room.roomId, target.eventId)}`;
        void shareText({ text: body }).then((outcome) => {
          // Запасной путь (Антон 05.10): если ни мост, ни Web Share не
          // сработали — ссылка копируется в буфер, а не молчок по кнопке.
          if (outcome === "unavailable") {
            void navigator.clipboard.writeText(permalinkOf(room.roomId, target.eventId));
          }
        });
      })}
      {canShareLink && menuItem(t("timeline.copyLink"), "msg-copy-link", () => {
        void navigator.clipboard.writeText(permalinkOf(room.roomId, target.eventId));
      })}
      {canCopy && menuItem(t("ui.kopirovat_tekst"), "msg-copy", () => {
        if (item) void navigator.clipboard.writeText(item.body);
      })}
      {onDelete && menuItem(t("common.remove"), "msg-delete", () => onDelete(target.eventId), true, !canDelete)}
    </div>
  );
}
